// src/pages/Admin.jsx - COMPLETE FIXED VERSION with defensive defaults
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { getSalonConfig, updateSalonConfig, getSalonServices } from '../api/salonApi';
import { logoutSalonUser } from '../api/salonAuthService';
import { useModules } from '../contexts/ModuleContext';
import { useSalonAuth } from '../contexts/SalonAuthContext';
import { doc, getDoc, getDocs, onSnapshot, collection, query, where, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';

// Import existing editor components (unchanged)
import BrandingEditor from '../components/admin/BrandingEditor';
import TypographyEditor from '../components/admin/TypographyEditor';
import ImageManager from '../components/admin/ImageManager';
import ContactInfoEditor from '../components/admin/ContactInfoEditor';
import BusinessHoursEditor from '../components/admin/BusinessHoursEditor';
import AboutUsEditor from '../components/admin/AboutUsEditor';
import CustomCSSEditor from '../components/admin/CustomCSSEditor';
import ServicesEditor from '../components/admin/ServicesEditor';
import ProductsEditor from '../components/admin/ProductsEditor';
import StaffEditor from '../components/admin/StaffEditor';
import BookingCalendarTab from '../components/admin/BookingCalendarTab';
import BookingRequestsTab from '../components/admin/BookingRequestsTab';
import WalletTopupsTab from '../components/admin/WalletTopupsTab';
import PromotionsEditor from '../components/admin/PromotionsEditor';
import LoyaltyProgramEditor from '../components/admin/LoyaltyProgramEditor';
import CustomersManager from '../components/admin/CustomersManager';
import NotificationsEditor from '../components/admin/NotificationsEditor';
import EmailCampaignsEditor from '../components/admin/EmailCampaignsEditor';
import PushCampaignsEditor from '../components/admin/PushCampaignsEditor';
import CampaignsListingEditor from '../components/admin/CampaignsListing';
import WhatsAppCampaignsEditor from '../components/admin/WhatsAppCampaignsEditor';
import SocialMediaCampaignsEditor from '../components/admin/SocialMediaCampaignsEditor';
import PortalPersonalisationEditor from '../components/admin/PortalPersonalisationEditor';
// WhatsAppConfigEditor removed — WhatsApp config is now managed by super admin
import QueryBuilderTab from '../components/admin/QueryBuilderTab';
import AnalyticsDashboardTab from '../components/admin/AnalyticsDashboardTab';
import LoyaltyAI from '../components/admin/LoyaltyAI';
import CommunicationsAI from '../components/admin/CommunicationsAI';
import BookingsAIAgent from '../components/admin/BookingsAIAgent';
import LoyaltyAIAgent from '../components/admin/LoyaltyAIAgent';
import CommunicationsAIAgent from '../components/admin/CommunicationsAIAgent';
import ReportsAIAgent from '../components/admin/ReportsAIAgent';
import CoreAIAgent from '../components/admin/CoreAIAgent';
import ModuleManager from '../components/admin/ModuleManager';
import FileIntegrationTab from '../components/admin/FileIntegrationTab';
import SalonHomePage from '../components/admin/SalonHomePage';
import TargetsEditor from '../components/admin/TargetsEditor';
import BookingLeaveManagement from '../components/admin/BookingLeaveManagement';
import WorkingHoursEditor from '../components/admin/WorkingHoursEditor';
import StaffPayrollTab from '../components/admin/StaffPayrollTab';
import OnboardingChecklist from '../components/admin/OnboardingChecklist'; // #Revised UI_v2 — A4
import QRScannerModal from '../components/admin/QRScannerModal';
import BespokeAIHub from '../components/admin/BespokeAIHub';
import { useSalonLocations } from '../hooks/useSalonLocations';
import { subscribeToProductAlerts, resolveProductAlert, getSalonProducts } from '../api/productsApi';
import { ICON_BASE_URL } from '../utils/moduleIcons';

import '../styles/notifications.css';
import '../styles/working-hours.css';
import '../styles/unsubscribe.css';
import '../styles/staffeditor.css';
import '../styles/salonhomepage.css';
import '../styles/targets-editor.css';
import '../styles/staff-payroll.css';
import '../styles/responsive-utils.css';
import '../styles/admin-responsive.css';
import '../styles/admin-overflow-fix.css';

// ENHANCED Fallback config with ALL required fields
const fallbackConfig = {
  salonId: 'demo-salon',
  branding: {
    name: 'iBeauty',
    logo: {
      url: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMDAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCAyMDAgODAiPgogIDxkZWZzPgogICAgPGxpbmVhckdyYWRpZW50IGlkPSJnb2xkR3JhZGllbnQiIHgxPSIwJSIgeTE9IjAlIiB4Mj0iMTAwJSIgeTI9IjEwMCUiPgogICAgICA8c3RvcCBvZmZzZXQ9IjAlIiBzdHlsZT0ic3RvcC1jb2xvcjojZmZlZGJmOyBzdG9wLW9wYWNpdHk6MSIgLz4KICAgICAgPHN0b3Agb2Zmc2V0PSI1MCUiIHN0eWxlPSJzdG9wLWNvbG9yOiNkNGFhMzQ7IHN0b3Atb3BhY2l0eToxIiAvPgogICAgICA8c3RvcCBvZmZzZXQ9IjEwMCUiIHN0eWxlPSJzdG9wLWNvbG9yOiNiODg2MGI7IHN0b3Atb3BhY2l0eToxIiAvPgogICAgPC9saW5lYXJHcmFkaWVudD4KICA8L2RlZnM+CiAgPHN0eWxlPgogICAgLnNjcmlwdCB7IGZvbnQtZmFtaWx5OiAnQ29ybW9yYW50IEdhcmFtb25kJywgc2VyaWY7IGZvbnQtc2l6ZTogMjhweDsgfQogICAgLnNlcmlmIHsgZm9udC1mYW1pbHk6ICdDb3Jtb3JhbnQgR2FyYW1vbmQnLCBzZXJpZjsgZm9udC1zaXplOiAxNnB4OyBsZXR0ZXItc3BhY2luZzogM3B4OyB9CiAgPC9zdHlsZT4KICA8cmVjdCB4PSIxMCIgeT0iMTUiIHdpZHRoPSIxODAiIGhlaWdodD0iNTAiIGZpbGw9Im5vbmUiIC8+CiAgPGcgZmlsbD0idXJsKCNnb2xkR3JhZGllbnQpIj4KICAgIDx0ZXh0IHg9IjEwMCIgeT0iNDAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGNsYXNzPSJzY3JpcHQiPmlCZWF1dHk8L3RleHQ+CiAgICA8dGV4dCB4PSIxMDAiIHk9IjU4IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBjbGFzcz0ic2VyaWYiPkxVWFVSWSBTQUxPTjwvdGV4dD4KICAgIDxsaW5lIHgxPSIzMCIgeTE9IjQ4IiB4Mj0iNzAiIHkyPSI0OCIgc3Ryb2tlPSJ1cmwoI2dvbGRHcmFkaWVudCkiIHN0cm9rZS13aWR0aD0iMSIgLz4KICAgIDxsaW5lIHgxPSIxMzAiIHkxPSI0OCIgeDI9IjE3MCIgeTI9IjQ4IiBzdHJva2U9InVybCgjZ29sZEdyYWRpZW50KSIgc3Ryb2tlLXdpZHRoPSIxIiAvPgogICAgPGNpcmNsZSBjeD0iNTAiIGN5PSIyOCIgcj0iNSIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ1cmwoI2dvbGRHcmFkaWVudCkiIHN0cm9rZS13aWR0aD0iMSIgLz4KICAgIDxjaXJjbGUgY3g9IjE1MCIgY3k9IjI4IiByPSI1IiBmaWxsPSJub25lIiBzdHJva2U9InVybCgjZ29sZEdyYWRpZW50KSIgc3Ryb2tlLXdpZHRoPSIxIiAvPgogIDwvZz4KPC9zdmc+',
      width: 200,
      height: 80,
      position: 'left'
    },
    colors: {
      primary: '#E64C31',
      secondary: '#FFBDB1',
      accent: '#F6F6F6',
      background: '#FFFFFF',
      text: '#333333'
    },
    typography: {
      headingFont: 'Familjen Grotesk, sans-serif',
      bodyFont: 'Wix Madefor Text, sans-serif',
      fontSize: 'medium'
    },
    images: {
      hero: '',
      gallery: []
    },
    customCSS: ''
  },
  contact: {
    address: '123 Demo Street',
    phone: '(123) 456-7890',
    email: 'demo@example.com',
    socialMedia: {
      facebook: '',
      instagram: '',
      twitter: ''
    }
  },
  businessHours: {
    regularHours: [
      { day: 'Monday', open: '09:00', close: '18:00', isClosed: false },
      { day: 'Tuesday', open: '09:00', close: '18:00', isClosed: false },
      { day: 'Wednesday', open: '09:00', close: '18:00', isClosed: false },
      { day: 'Thursday', open: '09:00', close: '18:00', isClosed: false },
      { day: 'Friday', open: '09:00', close: '18:00', isClosed: false },
      { day: 'Saturday', open: '10:00', close: '16:00', isClosed: false },
      { day: 'Sunday', open: '00:00', close: '00:00', isClosed: true }
    ],
    holidayClosures: [],
    customClosures: []
  },
  aboutUs: ''
};

// NEW: Helper function to deeply merge config with fallback
const mergeWithFallback = (config, fallback) => {
  if (!config) return JSON.parse(JSON.stringify(fallback));
  
  const merged = { ...fallback, ...config };
  
  // Ensure branding exists and has all required fields
  if (!merged.branding) {
    merged.branding = JSON.parse(JSON.stringify(fallback.branding));
  } else {
    merged.branding = {
      name: merged.branding.name || fallback.branding.name,
      logo: {
        ...fallback.branding.logo,
        ...(merged.branding.logo || {})
      },
      colors: {
        ...fallback.branding.colors,
        ...(merged.branding.colors || {})
      },
      typography: {
        ...fallback.branding.typography,
        ...(merged.branding.typography || {})
      },
      images: {
        hero: merged.branding.images?.hero || fallback.branding.images.hero,
        gallery: Array.isArray(merged.branding.images?.gallery) ? merged.branding.images.gallery : fallback.branding.images.gallery
      },
      customCSS: merged.branding.customCSS || fallback.branding.customCSS || ''
    };
  }
  
  // Ensure contact exists
  if (!merged.contact) {
    merged.contact = JSON.parse(JSON.stringify(fallback.contact));
  } else {
    merged.contact = {
      address: merged.contact.address || fallback.contact.address,
      phone: merged.contact.phone || fallback.contact.phone,
      email: merged.contact.email || fallback.contact.email,
      socialMedia: {
        ...fallback.contact.socialMedia,
        ...(merged.contact.socialMedia || {})
      }
    };
  }
  
  // Ensure businessHours exists
  if (!merged.businessHours) {
    merged.businessHours = JSON.parse(JSON.stringify(fallback.businessHours));
  } else if (!merged.businessHours.regularHours || !Array.isArray(merged.businessHours.regularHours)) {
    merged.businessHours = JSON.parse(JSON.stringify(fallback.businessHours));
  }
  
  // Ensure aboutUs exists
  if (merged.aboutUs === undefined || merged.aboutUs === null) {
    merged.aboutUs = '';
  }
  
  return merged;
};

// #Revised UI_v2 — A2: Grouped sidebar navigation — groups mirror the 6 module cards on the BespokeBeauty Management Modules page
const SIDEBAR_GROUPS = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: (
      <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="9" y="1" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="1" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
        <rect x="9" y="9" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.5"/>
      </svg>
    ),
    items: [
      { moduleId: 'home', tabId: null, label: 'Admin Home',  isOverview: false },
      { moduleId: null,   tabId: null, label: 'All Modules', isOverview: true  },
    ]
  },
  {
    // Mirrors "Core Module" card — Brand, Typography, Images, About, Contact, Hours, Services, Staff, Customers, CSS
    id: 'core',
    label: 'Core Module',
    icon: (
      <img className="tab-group-icon-img" src={`${ICON_BASE_URL}core-module.svg?alt=media&token=6a7e6302-93f8-4b42-9495-c9c26f3296da`} alt="Core Module" />
    ),
    items: [
      { moduleId: 'core', tabId: 'branding',   label: 'Brand Identity'  },
      { moduleId: 'core', tabId: 'typography', label: 'Typography'      },
      { moduleId: 'core', tabId: 'images',     label: 'Images'          },
      { moduleId: 'core', tabId: 'aboutUs',    label: 'About Us'        },
      { moduleId: 'core', tabId: 'contact',    label: 'Contact Info'    },
      { moduleId: 'core', tabId: 'hours',      label: 'Business Hours'  },
      { moduleId: 'core', tabId: 'services',   label: 'Services'        },
      { moduleId: 'core', tabId: 'products',   label: 'Products'        },
      { moduleId: 'core', tabId: 'staff',      label: 'Staff'           },
      { moduleId: 'core', tabId: 'customers',  label: 'Customers'       },
      { moduleId: 'core', tabId: 'wallet-topups',      label: 'Packages'          },
      { moduleId: 'core', tabId: 'file-integration', label: 'File Integration'  },
      { moduleId: 'core', tabId: 'customCSS',         label: 'Custom CSS'        },
      { moduleId: 'core', tabId: 'core-ai-agent',    label: 'Core AI',  requiresTabAvailable: true, requiresLabel: 'Core AI' },
    ]
  },
  {
    // Mirrors "Bookings Management" card
    id: 'bookings',
    label: 'Bookings Management',
    icon: (
      <img className="tab-group-icon-img" src={`${ICON_BASE_URL}booking-management.svg?alt=media&token=248a2ac6-7a00-4990-82b5-b354ede53ec7`} alt="Bookings Management" />
    ),
    items: [
      { moduleId: 'bookings', tabId: 'booking-calendar', label: 'Calendar Management' },
      { moduleId: 'bookings', tabId: 'booking-requests', label: 'Booking Requests'    },
      { moduleId: 'bookings', tabId: 'leave-management', label: 'Leave Management'    },
      { moduleId: 'bookings', tabId: 'working-hours',    label: 'Working Hours'       },
      // Part of Bespoke AI: shown dimmed unless Bespoke AI + its Bookings AI sub-module are enabled
      { moduleId: 'bookings', tabId: 'bookings-ai-agent', label: 'Bookings AI', requiresTabAvailable: true, requiresLabel: 'Bookings AI' },
    ]
  },
  {
    // Mirrors "Loyalty Management" card (module id is 'promotions')
    id: 'loyalty',
    label: 'Loyalty Management',
    icon: (
      <img className="tab-group-icon-img" src={`${ICON_BASE_URL}loyalty-management.svg?alt=media&token=1f626298-fb6c-4f59-a2b1-5b7c9fa404cd`} alt="Loyalty Management" />
    ),
    items: [
      { moduleId: 'promotions', tabId: 'loyalty-program', label: 'Loyalty Program' },
      { moduleId: 'promotions', tabId: 'promotions',      label: 'Promotions'      },
      // Part of Bespoke AI: shown dimmed unless Bespoke AI + its Loyalty AI sub-module are enabled
      { moduleId: 'promotions', tabId: 'loyalty-ai-agent', label: 'Loyalty AI', requiresTabAvailable: true, requiresLabel: 'Loyalty AI' },
    ]
  },
  {
    // Mirrors "Communications Management" card
    id: 'communications',
    label: 'Communications Management',
    icon: (
      <img className="tab-group-icon-img" src={`${ICON_BASE_URL}communication-management.svg?alt=media&token=e8485fc2-cf71-4d76-ab72-6e804212c6b1`} alt="Communications Management" />
    ),
    items: [
      { moduleId: 'communications', tabId: 'email-campaigns',    label: 'Email Campaigns'   },
      { moduleId: 'communications', tabId: 'push-campaigns',     label: 'Push Notification Campaigns'},
      { moduleId: 'communications', tabId: 'whatsapp-campaigns', label: 'WhatsApp Campaigns'},
      { moduleId: 'communications', tabId: 'social-media-campaigns', label: 'Social Media Campaigns'},
      { moduleId: 'communications', tabId: 'portal-personalisation', label: 'Portal Personalisation Campaigns'},
      { moduleId: 'communications', tabId: 'campaigns-listing',  label: 'Campaign History' },
      // Part of Bespoke AI: shown dimmed unless Bespoke AI + its Communications AI sub-module are enabled
      { moduleId: 'communications', tabId: 'communications-ai-agent', label: 'Communications AI', requiresTabAvailable: true, requiresLabel: 'Communications AI' },
    ]
  },
  {
    // Mirrors "Reports Management" card
    id: 'reports',
    label: 'Reports Management',
    icon: (
      <img className="tab-group-icon-img" src={`${ICON_BASE_URL}reports-management.svg?alt=media&token=bcfef9fc-6076-42f5-ad60-f633cc51af59`} alt="Reports Management" />
    ),
    items: [
      { moduleId: 'reports', tabId: 'query-builder',       label: 'Query Builder'      },
      { moduleId: 'reports', tabId: 'analytics-dashboard', label: 'Analytics Dashboard'},
      { moduleId: 'reports', tabId: 'targets',             label: 'Revenue Targets'    },
      { moduleId: 'reports', tabId: 'staff-payroll',       label: 'Staff Payroll'      },
      // Part of Bespoke AI: shown dimmed unless Bespoke AI + its Reports AI sub-module are enabled
      { moduleId: 'reports', tabId: 'reports-ai-agent', label: 'Reports AI', requiresTabAvailable: true, requiresLabel: 'Reports AI' },
    ]
  },
  {
    // Mirrors "Bespoke AI" card
    id: 'bespokeai',
    label: 'Bespoke AI',
    icon: (
      <img className="tab-group-icon-img" src={`${ICON_BASE_URL}bespoke-ai.svg?alt=media&token=f0b355ff-a133-4ded-a36a-dac8bd0061c5`} alt="Bespoke AI" />
    ),
    items: [
      { moduleId: 'communications', tabId: 'communications-ai-agent', label: 'Communications AI', requiresTabAvailable: true, requiresLabel: 'Communications AI' },
      // There is one Loyalty AI: the agent under Loyalty Management (it also answers every question the old
      // Bespoke AI > Loyalty AI Q&A tab did, plus can act on promotions/vouchers/programme).
      { moduleId: 'promotions', tabId: 'loyalty-ai-agent', label: 'Loyalty AI', requiresTabAvailable: true, requiresLabel: 'Loyalty AI' },
      // There is one Bookings AI: the agent under Bookings Management (it also answers every insight question the
      // old Bespoke AI > Bookings AI tab did). This entry opens it; it is last so clicking the Bespoke AI group
      // doesn't jump into Bookings Management.
      { moduleId: 'bookings', tabId: 'bookings-ai-agent', label: 'Bookings AI', requiresTabAvailable: true, requiresLabel: 'Bookings AI' },
      // Likewise the single Reports AI is the agent under Reports Management (it answers every question the old
      // Bespoke AI > Reports AI tab did, with the same charts and Excel export)
      { moduleId: 'reports', tabId: 'reports-ai-agent', label: 'Reports AI', requiresTabAvailable: true, requiresLabel: 'Reports AI' },
    ]
  }
];

// Deduplicate services by name — one card per unique service name, with global price range
const deduplicateServicesByName = (servicesList) => {
  const groups = {};
  servicesList.forEach((svc) => {
    const key = svc.name;
    if (!groups[key]) groups[key] = [];
    groups[key].push(svc);
  });

  return Object.values(groups).map((group) => {
    const base = group[0];
    const imageUrl = group.find((s) => s.imageUrl)?.imageUrl || null;

    const prices = group.map((s) => {
      const lo = s.priceMin ?? s.price ?? null;
      const hi = s.priceMax ?? s.price ?? null;
      return { lo, hi };
    }).filter((p) => p.lo != null);

    const globalMin = prices.length ? Math.min(...prices.map((p) => Number(p.lo))) : null;
    const globalMax = prices.length ? Math.max(...prices.map((p) => Number(p.hi ?? p.lo))) : null;

    return {
      ...base,
      imageUrl,
      priceMin: globalMin,
      priceMax: globalMax,
      price: undefined,
    };
  });
};

function Admin({ salonId: propSalonId, moduleHint }) {
  // Multi-tenant support and navigation
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { currentUser, loading: authLoading } = useSalonAuth(); // Get authenticated user
  
  // Priority: prop > auth context > URL param > fallback
  const salonId = propSalonId || currentUser?.salonId || params.salonId || 'demo-salon';
  
  console.log('🔍 [Admin] SalonId resolution:', {
    propSalonId,
    authSalonId: currentUser?.salonId,
    paramSalonId: params.salonId,
    finalSalonId: salonId
  });

  // Use module context
  const {
    moduleConfig,
    activeModule,
    activeTab,
    switchToModule,
    switchToTab,
    getActiveModuleTabs,
    isTabAvailable,
    isModuleEnabled: _isModuleEnabled,
    loadModuleConfig,
    switchToModuleByURL
  } = useModules();

  // User-level booking permission gate — combines client module config with user permission.
  // While auth is still loading, assume permission=true to avoid race-condition false negatives
  // where the user profile hasn't arrived from Firestore yet and canManageBookings evaluates
  // to undefined → false, triggering a spurious "Module Access Restricted" screen.
  // Also grant access by role: owner/admin/manager always have bookings access per DEFAULT_PERMISSIONS,
  // so even if the Firestore permissions doc has a stale/missing canManageBookings value the role
  // itself serves as proof of access.
  const canManageBookings = authLoading
    ? true  // don't gate during load — permissions not yet available
    : (['owner', 'admin', 'manager'].includes(currentUser?.role) ||
       currentUser?.permissions?.canManageBookings === true);

  const isModuleEnabled = (moduleId) => {
    if (moduleId === 'bookings' && !canManageBookings) return false;
    return _isModuleEnabled(moduleId);
  };

  const [salonConfig, setSalonConfig] = useState({ ...fallbackConfig, salonId });
  const [salonData, setSalonData] = useState(null); // Direct Firebase salon data for Live Preview
  const [loading, setLoading] = useState(true);

  // Shared booking location selector — persists across BookingCalendarTab ↔ BookingRequestsTab navigation
  const { locations: bookingLocations } = useSalonLocations(salonId);
  const [bookingLocationId, setBookingLocationId] = useState('');
  // Jump target set by the notification-modal "Review" button for BookingRequestsTab
  const [bookingRequestsJump, setBookingRequestsJump] = useState(null);
  // Initialize booking location selector to first location once loaded
  React.useEffect(() => {
    if (bookingLocations.length > 0 && !bookingLocationId) {
      setBookingLocationId(bookingLocations[0].locationId);
    }
  }, [bookingLocations]);

  // Initialize preview location selector to first location once salonData loads
  React.useEffect(() => {
    const locs = salonData?.locations || [];
    if (locs.length > 0 && !previewSelectedLocationId) {
      setPreviewSelectedLocationId(locs[0].locationId);
    }
  }, [salonData]);
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState('');
  const [services, setServices] = useState([]);
  const [previewProducts, setPreviewProducts] = useState([]); // Products shown in Live Preview
  // HTML email prefill from CommunicationsAI
  const [htmlEmailPrefill, setHtmlEmailPrefill] = useState({ html: '', subject: '', type: 'html' });
  const [whatsappPrefill, setWhatsappPrefill] = useState({ body: '' });
  const [pushPrefill, setPushPrefill] = useState({ subject: '', body: '' });
  // Campaign edit prefill — populated when user clicks Edit on a failed campaign in the listing
  const [campaignEditPrefill, setCampaignEditPrefill] = useState({ type: null, data: null });
  const [signingOut, setSigningOut] = useState(false);
  const [moduleAccessDenied, setModuleAccessDenied] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewSelectedLocationId, setPreviewSelectedLocationId] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // #Revised UI_v2 — TopNav: single selected group tab (replaces expandedGroups Set from A2 sidebar)
  const [activeGroupId, setActiveGroupId] = useState('dashboard');
  const [pendingTab, setPendingTab] = useState(null);
  // #Revised UI_v2 — A3: start: header enhancement state
  const [pendingCount, setPendingCount] = useState(0);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const profileMenuRef = useRef(null);
  const [showQRScanner, setShowQRScanner] = useState(false);
  // #Revised UI_v2 — A3: end
  // #Revised UI_v2 — A8: start: alert bar state
  const [upcomingLeaveCount, setUpcomingLeaveCount] = useState(0);
  const [pendingLeaveApprovalCount, setPendingLeaveApprovalCount] = useState(0);
  // #Revised UI_v2 — A8: end
  // Alerts modal state
  const [pendingBookings, setPendingBookings] = useState([]);
  const [pendingLeaveApprovals, setPendingLeaveApprovals] = useState([]);
  const [pendingConflictResolutions, setPendingConflictResolutions] = useState([]);
  const [upcomingLeaveItems, setUpcomingLeaveItems] = useState([]);
  const [productAlerts, setProductAlerts] = useState([]);
  const [customerCancellations, setCustomerCancellations] = useState([]);
  const [showAlertsModal, setShowAlertsModal] = useState(false);
  const [dismissedAlertIds, setDismissedAlertIds] = useState(new Set());
  // Use refs to prevent multiple loads
  const moduleConfigLoadedRef = useRef(false);
  const salonConfigLoadedRef = useRef(false);
  const servicesLoadedRef = useRef(false);
  const urlSyncRef = useRef(false);
  const targetTabHandledRef = useRef(false);

  // URL to Module mapping - using useCallback
  const getModuleFromPath = useCallback((pathname) => {
    if (pathname.includes('/admin/allmodules')) return null;
    if (pathname.includes('/admin/core')) return 'core';
    if (pathname.includes('/admin/bookings')) return 'bookings';
    if (pathname.includes('/admin/promotions')) return 'promotions';
    if (pathname.includes('/admin/communications')) return 'communications';
    if (pathname.includes('/admin/reports')) return 'reports';
    if (pathname.includes('/admin/ai')) return 'bespokeai';
    if (pathname.includes('/admin/home')) return 'home';

    // LEGACY URL SUPPORT
    if (pathname.includes('/admin/book')) return 'bookings';
    if (pathname.includes('/admin/promotion')) return 'promotions';
    if (pathname.includes('/admin/campaigns')) return 'communications';
    if (pathname.includes('/admin/analytics')) return 'reports';

    return 'home';
  }, []);

  // Module to URL mapping - using useCallback
  const getPathFromModule = useCallback((moduleId) => {
    const moduleToPath = {
      'bookings': 'bookings',
      'promotions': 'promotions',
      'communications': 'communications',
      'reports': 'reports',
      'bespokeai': 'ai',
      'home': 'home',
      'core': 'core'
    };
    return moduleToPath[moduleId] || 'home';
  }, []);

  // Check if current view is module overview
  const isModuleOverview = activeModule === null;
  
  // Check if current tab is full-width (no preview)
  const isFullWidthTab = [
    'booking-calendar',
    'booking-requests',
    'loyalty-program',
    'promotions',
    'wallet-topups',
    'file-integration',
    'customers',
    'products',
    'query-builder',
    'analytics-dashboard',
    'email-campaigns',
    'push-campaigns',
    'portal-personalisation',
    'whatsapp-campaigns',
    'social-media-campaigns',
    'campaigns-listing',
    'loyalty-ai',
    'communications-ai',
    'targets',
    'leave-management',
    'working-hours',
    'bookings-ai-agent',
    'loyalty-ai-agent',
    'communications-ai-agent',
    'reports-ai-agent',
    'core-ai-agent',
    'staff-payroll',
    'file-integration'
  ].includes(activeTab);

  // Enhanced module navigation - using useCallback
  const handleModuleNavigation = useCallback((moduleId) => {
    if (moduleId === null) {
      navigate(`/BespokeBeauty/${salonId}/admin/allmodules`);
      switchToModule(null);
      setModuleAccessDenied(false);
    } else {
      if (!isModuleEnabled(moduleId)) {
        console.log(`Access denied for module: ${moduleId}`);
        setModuleAccessDenied(true);
        return;
      }
      
      const path = getPathFromModule(moduleId);
      navigate(`/BespokeBeauty/${salonId}/admin/${path}`);
      const success = switchToModule(moduleId);
      
      if (!success) {
        setModuleAccessDenied(true);
      } else {
        setModuleAccessDenied(false);
      }
    }
  }, [navigate, salonId, switchToModule, isModuleEnabled, getPathFromModule]);

  // Enhanced tab switching - using useCallback
  const handleTabSwitch = useCallback((tabId) => {
    if (!isTabAvailable(tabId)) {
      console.log(`Tab ${tabId} is not available`);
      return;
    }
    switchToTab(tabId);
  }, [isTabAvailable, switchToTab]);

  // Sign out handler - using useCallback
  const handleSignOut = useCallback(async () => {
    try {
      setSigningOut(true);
      await logoutSalonUser();
      navigate(`/BespokeBeauty/${salonId}/admin/login`);
    } catch (error) {
      console.error('Error signing out:', error);
      setSigningOut(false);
    }
  }, [navigate, salonId]);

  // Menu toggle functions
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // Campaign creation handler - using useCallback
  const handleCreateCampaign = useCallback((targetModule, campaignType) => {
    console.log('Handling campaign creation:', { targetModule, campaignType });
    
    if (!isModuleEnabled(targetModule)) {
      console.error(`Cannot create campaign: Module ${targetModule} is not enabled`);
      setModuleAccessDenied(true);
      return;
    }
    
    const path = getPathFromModule(targetModule);
    navigate(`/BespokeBeauty/${salonId}/admin/${path}`);
    
    const moduleSuccess = switchToModule(targetModule);
    if (!moduleSuccess) {
      console.error(`Failed to switch to module: ${targetModule}`);
      setModuleAccessDenied(true);
      return;
    }
    
    let targetTab;
    if (campaignType === 'email') {
      targetTab = 'email-campaigns';
    } else if (campaignType === 'push') {
      targetTab = 'push-campaigns';
    } else {
      targetTab = 'campaigns-listing';
    }
    
    sessionStorage.setItem('targetTab', targetTab);
    
    setTimeout(() => {
      if (isTabAvailable(targetTab)) {
        console.log('Switching to target tab:', targetTab);
        switchToTab(targetTab);
      } else {
        console.log('Target tab not available, using default communications tab');
        const communicationsTabs = getActiveModuleTabs();
        if (communicationsTabs.length > 0) {
          switchToTab(communicationsTabs[0]);
        }
      }
    }, 100);
    
  }, [navigate, salonId, switchToModule, switchToTab, isModuleEnabled, isTabAvailable, getPathFromModule, getActiveModuleTabs]);

  // Handle AI-generated email → navigate to email editor with prefilled content
  const handleUseInEmailEditor = useCallback((content, subject, type = 'html') => {
    setHtmlEmailPrefill({ html: type === 'html' ? (content || '') : '', subject: subject || '', type, textBody: type === 'text' ? (content || '') : '' });
    navigate(`/BespokeBeauty/${salonId}/admin/communications`);
    switchToModule('communications');
    setTimeout(() => {
      switchToTab('email-campaigns');
    }, 200);
  }, [switchToModule, switchToTab, navigate, salonId]);

  // Handle AI-generated WhatsApp message → navigate to WhatsApp editor
  const handleUseInWhatsAppEditor = useCallback((body) => {
    setWhatsappPrefill({ body: body || '' });
    navigate(`/BespokeBeauty/${salonId}/admin/communications`);
    switchToModule('communications');
    setTimeout(() => {
      switchToTab('whatsapp-campaigns');
    }, 200);
  }, [switchToModule, switchToTab, navigate, salonId]);

  // Handle AI-generated push notification → navigate to push editor
  const handleUseInPushEditor = useCallback((subject, body) => {
    setPushPrefill({ subject: subject || '', body: body || '' });
    navigate(`/BespokeBeauty/${salonId}/admin/communications`);
    switchToModule('communications');
    setTimeout(() => {
      switchToTab('push-campaigns');
    }, 200);
  }, [switchToModule, switchToTab, navigate, salonId]);

  // Handle Products "Find Prospects" → campaign editor with pre-populated product content
  const handleProductCampaign = useCallback((channelType, productData, matchedCustomers) => {
    if (!isModuleEnabled('communications')) {
      setModuleAccessDenied(true);
      return;
    }

    const { productName, productPrice, productDesc, paynowQrUrl } = productData;
    const qrBlock = paynowQrUrl
      ? `<p style="margin-top:16px"><strong>Pay via PayNow:</strong><br/><img src="${paynowQrUrl}" alt="PayNow QR" style="width:180px;height:180px;border:1px solid #eee;border-radius:8px;margin-top:8px"/></p>`
      : '<p style="margin-top:16px;color:#888">PayNow QR code not configured. Please set it up in your account settings.</p>';

    const emailHtml = `<div style="font-family:sans-serif;max-width:600px;margin:0 auto">
<h2 style="color:#E64C31">Exclusive Product Offer</h2>
<p>Dear Customer,</p>
<p>We're excited to bring you <strong>${productName}</strong>${productPrice ? ` at <strong>${productPrice}</strong>` : ''}.</p>
${productDesc ? `<p>${productDesc}</p>` : ''}
<p>Visit us in store to purchase, or pay conveniently via PayNow:</p>
${qrBlock}
<p style="margin-top:24px">We look forward to seeing you soon!</p>
</div>`;

    const emailSubject = `Exclusive Product Offer — ${productName}`;

    const whatsappBody = `Hello! We thought you'd love *${productName}*${productPrice ? ` at *${productPrice}*` : ''}.${productDesc ? ` ${productDesc}` : ''} Visit us to purchase!${paynowQrUrl ? ` Pay via PayNow: ${paynowQrUrl}` : ''}`;

    const pushTitle = `Exclusive Offer — ${productName}`;
    const pushBody = `Get ${productName}${productPrice ? ` for ${productPrice}` : ''}. Visit us today!`;

    const tabMap = { email: 'email-campaigns', push: 'push-campaigns', whatsapp: 'whatsapp-campaigns' };
    // Pass full customer objects so campaign editors can populate selectedCustomers directly.
    const targetingCriteria = matchedCustomers.length > 0
      ? { specificCustomers: matchedCustomers }
      : {};
    const targetingType = matchedCustomers.length > 0 ? 'specific_customer' : 'all_customers';

    if (channelType === 'email') {
      setHtmlEmailPrefill({ html: emailHtml, subject: emailSubject, type: 'html' });
      setCampaignEditPrefill({
        type: 'email',
        data: {
          subject: emailSubject,
          html: emailHtml,
          // Top-level fields consumed by the editor's prefillCampaign effect
          targetingType,
          targetingCriteria,
          // metadata keeps the same shape as Firestore-stored campaigns (used by CampaignsListing)
          metadata: { targetingType, targetingCriteria }
        }
      });
    } else if (channelType === 'whatsapp') {
      setWhatsappPrefill({ body: whatsappBody });
      setCampaignEditPrefill({
        type: 'whatsapp',
        data: {
          body: whatsappBody,
          targetingType,
          targetingCriteria,
          metadata: { targetingType, targetingCriteria }
        }
      });
    } else if (channelType === 'push') {
      setPushPrefill({ subject: pushTitle, body: pushBody });
      setCampaignEditPrefill({
        type: 'push',
        data: {
          title: pushTitle,
          body: pushBody,
          targetingType,
          targetingCriteria,
          metadata: { targetingType, targetingCriteria }
        }
      });
    }

    navigate(`/BespokeBeauty/${salonId}/admin/communications`);
    switchToModule('communications');
    setTimeout(() => {
      switchToTab(tabMap[channelType]);
    }, 200);
  }, [navigate, salonId, switchToModule, switchToTab, isModuleEnabled]);

  // Handle Edit click on a failed campaign in Campaign Listing → open the relevant editor pre-populated
  const handleEditCampaignFromListing = useCallback((campaignType, editData) => {
    setCampaignEditPrefill({ type: campaignType, data: editData });
    const tabMap = { email: 'email-campaigns', push: 'push-campaigns', whatsapp: 'whatsapp-campaigns' };
    const targetTab = tabMap[campaignType];
    if (targetTab) {
      setTimeout(() => switchToTab(targetTab), 100);
    }
  }, [switchToTab]);

  // #Revised UI_v2 — A2: start: sidebar navigation handler (cross-module aware)
  const handleSidebarNavigation = useCallback((moduleId, tabId, isOverview = false) => {
    if (isOverview || moduleId === null) {
      handleModuleNavigation(null);
      return;
    }
    if (moduleId === 'home') {
      handleModuleNavigation('home');
      return;
    }
    if (!isModuleEnabled(moduleId)) {
      setModuleAccessDenied(true);
      return;
    }
    if (moduleId === activeModule) {
      handleTabSwitch(tabId);
    } else {
      setPendingTab(tabId);
      handleModuleNavigation(moduleId);
    }
  }, [activeModule, handleModuleNavigation, handleTabSwitch, isModuleEnabled]);
  // #Revised UI_v2 — A2: end

  // Handle target tab from sessionStorage
  useEffect(() => {
    if (targetTabHandledRef.current) return;

    const handleTargetTab = () => {
      const targetTab = sessionStorage.getItem('targetTab');
      if (targetTab && activeModule && !isModuleOverview) {
        console.log('Found target tab in sessionStorage:', targetTab);

        const moduleTabs = getActiveModuleTabs();
        if (moduleTabs.includes(targetTab)) {
          console.log('Switching to target tab:', targetTab);
          switchToTab(targetTab);

          sessionStorage.removeItem('targetTab');
          targetTabHandledRef.current = true;
        } else {
          console.log('Target tab not available in current module:', targetTab);
          sessionStorage.removeItem('targetTab');
        }
      }
    };

    if (activeModule && moduleConfigLoadedRef.current) {
      handleTargetTab();
    }
  }, [activeModule, isModuleOverview, getActiveModuleTabs, switchToTab]);

  // Handle target tab from location state (e.g., when navigating from customer details)
  useEffect(() => {
    const stateTab = location.state?.activeTab;
    if (stateTab && activeModule && !isModuleOverview && moduleConfigLoadedRef.current) {
      console.log('Found target tab in location.state:', stateTab);

      const moduleTabs = getActiveModuleTabs();
      if (moduleTabs.includes(stateTab)) {
        console.log('Switching to target tab from state:', stateTab);
        switchToTab(stateTab);

        // Clear the state after applying it to prevent it from persisting
        navigate(location.pathname, { replace: true, state: {} });
      } else {
        console.log('Target tab not available in current module:', stateTab);
      }
    }
  }, [location.state, activeModule, isModuleOverview, getActiveModuleTabs, switchToTab, navigate, location.pathname]);

  // Load module configuration ONCE
  useEffect(() => {
    if (moduleConfigLoadedRef.current) return;
    
    const loadModuleConfigWithErrorHandling = async () => {
      try {
        moduleConfigLoadedRef.current = true;
        await loadModuleConfig(salonId);
        console.log('✓ Successfully loaded module configuration for salon:', salonId);
      } catch (error) {
        console.error('Failed to load module configuration for salon:', salonId, error);
      }
    };

    loadModuleConfigWithErrorHandling();
  }, [salonId, loadModuleConfig]);

  // Load salon configuration ONCE - ENHANCED with defensive merging
  useEffect(() => {
    if (salonConfigLoadedRef.current) return;
    
    const loadConfig = async () => {
      try {
        setLoading(true);
        salonConfigLoadedRef.current = true;
        
        console.log('Loading salon configuration for:', salonId);
        const config = await getSalonConfig(salonId);
        
        if (config) {
          console.log('✓ Loaded config successfully');
          config.salonId = salonId;
          
          // Handle aboutUs properly
          if (config.aboutUs === undefined || config.aboutUs === null) {
            config.aboutUs = '';
          } else if (typeof config.aboutUs === 'object') {
            if (config.aboutUs.toString() === '[object Object]') {
              const keys = Object.keys(config.aboutUs);
              if (keys.length > 0) {
                if (config.aboutUs.text) {
                  config.aboutUs = config.aboutUs.text;
                } else if (config.aboutUs.content) {
                  config.aboutUs = config.aboutUs.content;
                } else {
                  const stringValues = keys
                    .map(key => config.aboutUs[key])
                    .filter(value => typeof value === 'string')
                    .join(' ');
                  config.aboutUs = stringValues || '';
                }
              } else {
                config.aboutUs = '';
              }
            }
          } else if (typeof config.aboutUs !== 'string') {
            config.aboutUs = String(config.aboutUs);
          }

          // Handle business hours
          if (!config.businessHours) {
            config.businessHours = fallbackConfig.businessHours;
          }

          // CRITICAL: Merge with fallback to ensure all fields exist
          const mergedConfig = mergeWithFallback(config, fallbackConfig);
          mergedConfig.salonId = salonId;
          
          console.log('Merged config with fallback values - all fields guaranteed');
          setSalonConfig(mergedConfig);
        } else {
          console.log('No config found, using fallback with current salonId');
          const fallbackWithSalonId = { ...fallbackConfig, salonId };
          setSalonConfig(fallbackWithSalonId);
        }
      } catch (error) {
        console.error("Failed to load salon config for", salonId, ":", error);
        const fallbackWithSalonId = { ...fallbackConfig, salonId };
        setSalonConfig(fallbackWithSalonId);
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [salonId]);

  // Load salon data directly from Firebase for Live Preview
  useEffect(() => {
    if (!salonId) return;

    console.log('Setting up real-time listener for salon data:', salonId);

    const salonDocRef = doc(db, 'salons', salonId);

    // Set up real-time listener
    const unsubscribe = onSnapshot(salonDocRef, (docSnapshot) => {
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        console.log('Salon data loaded from Firebase:', data);
        setSalonData(data);
      } else {
        console.log('No salon document found in Firebase');
        setSalonData(null);
      }
    }, (error) => {
      console.error('Error fetching salon data:', error);
      setSalonData(null);
    });

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [salonId]);

  // Load services ONCE
  useEffect(() => {
    if (servicesLoadedRef.current) return;
    
    const loadServices = async () => {
      try {
        servicesLoadedRef.current = true;
        const loadedServices = await getSalonServices(salonId);
        setServices(loadedServices);
      } catch (err) {
        console.error('Error loading services for preview for salon:', salonId, err);
      }
    };

    loadServices();
  }, [salonId]);

  // Load products for Live Preview (initial load + reload after Products saved)
  useEffect(() => {
    if (!salonId) return;
    if (savedMessage && !savedMessage.includes('Products saved')) return;
    let cancelled = false;
    getSalonProducts(salonId)
      .then((loaded) => { if (!cancelled) setPreviewProducts(loaded); })
      .catch((err) => console.error('Error loading products for preview:', err));
    return () => { cancelled = true; };
  }, [salonId, savedMessage]);

  // URL synchronization effect
  useEffect(() => {
    
    const syncUrl = () => {
      const currentModule = getModuleFromPath(location.pathname);
      const targetModule = moduleHint || currentModule;
      
      if (moduleHint === 'allmodules') {
        switchToModule(null);
        setModuleAccessDenied(false);
      } else if (targetModule && targetModule !== activeModule) {
        console.log('Switching to module based on URL:', targetModule);
        
        if (targetModule !== 'home' && !isModuleEnabled(targetModule)) {
          console.log(`Module ${targetModule} is not enabled, redirecting to all modules`);
          navigate(`/BespokeBeauty/${salonId}/admin/allmodules`);
          switchToModule(null);
          setModuleAccessDenied(false);
          return;
        }
        
        const success = switchToModule(targetModule);
        if (!success) {
          navigate(`/BespokeBeauty/${salonId}/admin/allmodules`);
          switchToModule(null);
        }
        setModuleAccessDenied(!success);
      }
      
    };

    if (moduleConfigLoadedRef.current) {
      syncUrl();
    }
  }, [location.pathname, moduleHint, activeModule, getModuleFromPath, switchToModule, isModuleEnabled, navigate, salonId]);

  // Reload services when savedMessage changes
  useEffect(() => {
    if (savedMessage && savedMessage.includes('Services saved')) {
      const reloadServices = async () => {
        try {
          const loadedServices = await getSalonServices(salonId);
          setServices(loadedServices);
        } catch (err) {
          console.error('Error reloading services:', err);
        }
      };
      reloadServices();
    }
  }, [savedMessage, salonId]);

  // #Revised UI_v2 — A2: start: apply pending tab after cross-module navigation
  useEffect(() => {
    if (!pendingTab || !activeModule || isModuleOverview) return;
    const moduleTabs = getActiveModuleTabs();
    if (moduleTabs.includes(pendingTab)) {
      switchToTab(pendingTab);
      setPendingTab(null);
    }
  }, [activeModule, pendingTab, getActiveModuleTabs, switchToTab, isModuleOverview]);
  // #Revised UI_v2 — A2: end

  // #Revised UI_v2 — TopNav: auto-select the group tab that contains the active module/tab
  useEffect(() => {
    if (!activeModule || activeModule === 'home' || isModuleOverview) {
      setActiveGroupId('dashboard');
      return;
    }
    for (const group of SIDEBAR_GROUPS) {
      const hasItem = group.items.some(
        item => item.moduleId === activeModule &&
                (activeTab === null || item.tabId === activeTab)
      );
      if (hasItem) {
        setActiveGroupId(group.id);
        break;
      }
    }
  }, [activeModule, activeTab, isModuleOverview]);

  // #Revised UI_v2 — A3: start: notification count + profile menu click-outside
  useEffect(() => {
    if (!salonId) return;
    const q = query(
      collection(db, 'appointments'),
      where('salonId', '==', salonId),
      where('status', '==', 'pending')
    );
    const unsub = onSnapshot(q, (snap) => {
      const bookings = snap.docs.map(d => {
        const data = d.data();
        const ts = data.dateTimeRequested?.toDate
          ? data.dateTimeRequested.toDate()
          : data.dateTimeRequested
          ? new Date(data.dateTimeRequested)
          : null;
        return {
          id: d.id,
          customerName: data.customerName || data.customerEmail || 'Customer',
          customerEmail: data.customerEmail || '',
          serviceName: data.serviceRequested?.name || data.serviceName || 'Service',
          staffName: data.staffName || '',
          dateRequested: ts,
          // Fields used by the notification-modal Review button to pre-set filters
          locationId: data.locationId || null,
          apptDate: ts, // the scheduled appointment date (dateTimeRequested)
        };
      });
      setPendingBookings(bookings);
      setPendingCount(bookings.length);
    }, () => {});
    return () => unsub();
  }, [salonId]);

  // Customer self-cancellations (cancelledBy === 'customer') from the last 14 days, for the alert bell.
  // Plain equality filters only, so no composite index is needed; the date window is applied in memory.
  useEffect(() => {
    if (!salonId) return undefined;
    const q = query(
      collection(db, 'appointments'),
      where('salonId', '==', salonId),
      where('status', '==', 'cancelled'),
      where('cancelledBy', '==', 'customer')
    );
    const unsub = onSnapshot(q, (snap) => {
      const cutoff = Date.now() - 14 * 24 * 60 * 60 * 1000;
      const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
      const items = snap.docs.map(d => {
        const data = d.data();
        return {
          id: `cancel_${d.id}`,
          customerName: data.customerName || data.customerEmail || 'Customer',
          customerEmail: data.customerEmail || '',
          serviceName: data.serviceRequested?.name || data.serviceName || 'Service',
          staffName: data.staffName || '',
          appointmentDate: toDate(data.dateTimeRequested),
          cancelledAt: toDate(data.cancelledAt)
        };
      }).filter(c => c.cancelledAt && c.cancelledAt.getTime() >= cutoff)
        .sort((a, b) => b.cancelledAt - a.cancelledAt);
      setCustomerCancellations(items);
    }, () => {});
    return () => unsub();
  }, [salonId]);

  useEffect(() => {
    if (!showProfileMenu) return;
    const handleClickOutside = (e) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProfileMenu]);
  // #Revised UI_v2 — A3: end

  // #Revised UI_v2 — A8: start: upcoming staff leave count (next 7 days, approved only)
  useEffect(() => {
    if (!salonId) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);

    const q = query(
      collection(db, 'leave'),
      where('salonId', '==', salonId),
      where('startDate', '>=', today),
      where('startDate', '<=', nextWeek)
    );
    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs
        .filter(d => (d.data().status === 'approved' || !d.data().status))
        .map(d => {
          const data = d.data();
          return {
            id: d.id,
            staffName: data.staffName || data.staffId || 'Staff',
            type: data.type,
            startDate: data.startDate?.seconds ? new Date(data.startDate.seconds * 1000) : null,
            endDate: data.endDate?.seconds ? new Date(data.endDate.seconds * 1000) : null,
          };
        });
      const staffIds = new Set(items.map(i => i.staffName).filter(Boolean));
      setUpcomingLeaveCount(staffIds.size);
      setUpcomingLeaveItems(items);
    }, () => {});
    return () => unsub();
  }, [salonId]);
  // #Revised UI_v2 — A8: end

  // Pending leave approval count — for managers/admins/owners
  useEffect(() => {
    if (!salonId) return;
    const userRole = currentUser?.role;
    if (!userRole || !['owner', 'admin', 'manager'].includes(userRole)) return;
    const q = query(collection(db, 'leave'), where('salonId', '==', salonId));
    const unsub = onSnapshot(q, (snap) => {
      const items = snap.docs
        .filter(d => d.data().status === 'pending')
        .map(d => {
          const data = d.data();
          return {
            id: d.id,
            staffName: data.staffName || data.staffId || 'Staff',
            type: data.type,
            startDate: data.startDate?.seconds ? new Date(data.startDate.seconds * 1000) : null,
            endDate: data.endDate?.seconds ? new Date(data.endDate.seconds * 1000) : null,
            reason: data.reason || '',
          };
        });
      setPendingLeaveApprovalCount(items.length);
      setPendingLeaveApprovals(items);

      // Conflict resolutions needed: any leave with unresolved appointment conflicts.
      // Deduplicate by staffId + startDate + endDate + type — duplicate leave docs
      // (same staff, same period) should only produce one conflict row in the UI.
      const conflictItems = snap.docs
        .filter(d => d.data().hasConflicts && !d.data().conflictsResolved)
        .map(d => {
          const data = d.data();
          const startDate = data.startDate?.seconds ? new Date(data.startDate.seconds * 1000) : null;
          const endDate   = data.endDate?.seconds   ? new Date(data.endDate.seconds   * 1000) : null;
          return {
            id: d.id,
            staffId: data.staffId || '',
            staffName: data.staffName || '',   // may be empty — resolved below
            type: data.type,
            startDate,
            endDate,
            conflictCount: (data.conflictingAppointments || []).length,
            conflictingAppointments: data.conflictingAppointments || [],
            _dedupeKey: `${data.staffId}|${startDate?.toDateString()}|${endDate?.toDateString()}|${data.type}`,
          };
        });

      // Keep one item per unique dedupeKey — prefer the one with more conflict data.
      // Also collect ALL sibling doc IDs so that resolution can mark every duplicate resolved.
      const conflictSeen = new Map();
      conflictItems.forEach(item => {
        const existing = conflictSeen.get(item._dedupeKey);
        if (!existing) {
          conflictSeen.set(item._dedupeKey, { ...item, _allIds: [item.id] });
        } else {
          existing._allIds.push(item.id);
          if (item.conflictCount > existing.conflictCount) {
            conflictSeen.set(item._dedupeKey, { ...item, _allIds: existing._allIds });
          }
        }
      });
      const deduped = [...conflictSeen.values()];

      // Resolve missing staff names: if staffName is blank, look up the staff doc
      const needsResolution = deduped.filter(item => !item.staffName && item.staffId);
      if (needsResolution.length > 0) {
        (async () => {
          const staffSnap = await getDocs(
            query(collection(db, 'staff'), where('salonId', '==', salonId))
          );
          const nameById = {};
          staffSnap.forEach(d => {
            const sd = d.data();
            const key = sd.staffId || d.id;
            if (sd.name) nameById[key] = sd.name;
            if (d.id && sd.name) nameById[d.id] = sd.name;
          });
          setPendingConflictResolutions(deduped.map(item => ({
            ...item,
            staffName: item.staffName || nameById[item.staffId] || 'Staff',
          })));
        })();
      } else {
        setPendingConflictResolutions(deduped);
      }
    }, () => {});
    return () => unsub();
  }, [salonId, currentUser?.role]);

  // Subscribe to product alerts (out-of-stock + renewal approaching)
  useEffect(() => {
    if (!salonId) return;
    const unsubscribe = subscribeToProductAlerts(salonId, (alerts) => {
      setProductAlerts(alerts);
    });
    return () => unsubscribe();
  }, [salonId]);

  // Load dismissed alert IDs from localStorage
  useEffect(() => {
    if (!salonId) return;
    try {
      const stored = localStorage.getItem(`dismissedAlerts_${salonId}`);
      if (stored) setDismissedAlertIds(new Set(JSON.parse(stored)));
    } catch {}
  }, [salonId]);

  const dismissAlert = (id) => {
    setDismissedAlertIds(prev => {
      const next = new Set(prev);
      next.add(id);
      try {
        localStorage.setItem(`dismissedAlerts_${salonId}`, JSON.stringify([...next]));
      } catch {}
      return next;
    });
  };

  // ── Inline conflict resolution inside the alerts modal ────────────────────
  const [modalResolvingId,      setModalResolvingId]      = useState(null);
  const [modalStaffOptions,     setModalStaffOptions]     = useState({}); // { apptId: [staff] }
  const [modalSelections,       setModalSelections]       = useState({}); // { apptId: {action,staffId,staffName} }
  const [modalResolutionReason, setModalResolutionReason] = useState('');
  const [modalResolvingSaving,  setModalResolvingSaving]  = useState(false);
  const [modalResolveError,     setModalResolveError]     = useState('');
  const [modalStaffLoading,     setModalStaffLoading]     = useState(false);

  const openModalConflict = async (leaveItem) => {
    setModalResolvingId(leaveItem.id);
    setModalSelections({});
    setModalResolutionReason('');
    setModalResolveError('');
    setModalStaffOptions({});
    setModalStaffLoading(true);

    const appointments = leaveItem.conflictingAppointments || [];
    if (!appointments.length) { setModalStaffLoading(false); return; }

    try {
      // Load staff, services, leave, and active appointments in parallel
      const [staffSnap, svcSnap, leaveSnap, apptSnap] = await Promise.all([
        getDocs(query(collection(db, 'staff'), where('salonId', '==', salonId))),
        getDocs(query(collection(db, 'services'), where('salonId', '==', salonId))),
        getDocs(query(collection(db, 'leave'),    where('salonId', '==', salonId), where('status', '==', 'approved'))),
        getDocs(query(collection(db, 'appointments'), where('salonId', '==', salonId), where('status', 'in', ['approved', 'pending']))),
      ]);

      // Build staff list (active only, excluding the leave-taking staff member)
      const allStaff = [];
      staffSnap.forEach(d => {
        const data = d.data();
        if (data.isActive === false) return;
        if ((data.staffId || d.id) === leaveItem.staffId) return;
        allStaff.push({
          id:         d.id,
          staffId:    data.staffId || d.id,
          name:       data.name || 'Unnamed',
          services:   data.services   || [],
          locationIds: data.locationIds || [],
        });
      });

      // Build service-name → [serviceId] map for capability check
      const svcNameToIds = {};
      svcSnap.forEach(d => {
        const data = d.data();
        const id   = data.serviceId || d.id;
        const name = data.name;
        if (name) {
          if (!svcNameToIds[name]) svcNameToIds[name] = [];
          svcNameToIds[name].push(id);
        }
      });

      // Build staffId → leave date ranges for the leave-on-that-day check
      const staffLeaveRanges = {};
      leaveSnap.forEach(d => {
        const data = d.data();
        if (!data.staffId) return;
        const ls = data.startDate?.seconds ? new Date(data.startDate.seconds * 1000) : null;
        const le = data.endDate?.seconds   ? new Date(data.endDate.seconds * 1000)   : null;
        if (!ls || !le) return;
        if (!staffLeaveRanges[data.staffId]) staffLeaveRanges[data.staffId] = [];
        staffLeaveRanges[data.staffId].push({ start: ls, end: le });
      });

      // Build staffId → Set<time-bucket> for the same-slot appointment check
      // Time bucket = floor to nearest hour (ms)
      const staffApptBuckets = {};
      apptSnap.forEach(d => {
        const data = d.data();
        if (!data.staffId) return;
        const dt = data.dateTimeRequested?.toDate?.()
          ?? data.startDateTime?.toDate?.()
          ?? (data.dateTimeRequested ? new Date(data.dateTimeRequested) : null);
        if (!dt || isNaN(dt.getTime())) return;
        const bucket = Math.floor(dt.getTime() / (60 * 60 * 1000));
        if (!staffApptBuckets[data.staffId]) staffApptBuckets[data.staffId] = new Set();
        staffApptBuckets[data.staffId].add(bucket);
      });

      // For each conflicting appointment, build the available staff list
      const staffMap = {};
      for (const appt of appointments) {
        const serviceIds = svcNameToIds[appt.serviceName] || [];

        // 1. Service capability filter
        let eligible = allStaff.filter(s => {
          if (!serviceIds.length) return true; // unknown service → show all
          return serviceIds.some(sid => s.services.includes(sid));
        });

        // 2. Location filter (only if appointment has a locationId)
        if (appt.locationId) {
          const locMatch = eligible.filter(s =>
            s.locationIds.length === 0 || s.locationIds.includes(appt.locationId)
          );
          if (locMatch.length) eligible = locMatch;
        }

        // 3. Leave conflict check for the appointment date
        const apptDate = appt.dateTime?.toDate
          ? appt.dateTime.toDate()
          : appt.dateTime ? new Date(appt.dateTime) : null;
        const apptDayStart = apptDate ? new Date(apptDate.getFullYear(), apptDate.getMonth(), apptDate.getDate()) : null;
        const apptDayEnd   = apptDate ? new Date(apptDate.getFullYear(), apptDate.getMonth(), apptDate.getDate(), 23, 59, 59, 999) : null;

        // 4. Same-slot appointment check
        const apptBucket = apptDate ? Math.floor(apptDate.getTime() / (60 * 60 * 1000)) : null;

        const enriched = eligible.map(s => {
          let unavailReason = '';
          if (apptDayStart && apptDayEnd) {
            const ranges = staffLeaveRanges[s.staffId] || [];
            if (ranges.some(r => apptDayStart <= r.end && apptDayEnd >= r.start)) {
              unavailReason = 'on leave';
            }
          }
          if (!unavailReason && apptBucket !== null) {
            const buckets = staffApptBuckets[s.staffId] || new Set();
            if (buckets.has(apptBucket)) unavailReason = 'has another appointment';
          }
          return { ...s, unavailReason };
        });

        // Sort: available first, then unavailable with reason shown
        enriched.sort((a, b) => (a.unavailReason ? 1 : 0) - (b.unavailReason ? 1 : 0));
        staffMap[appt.id] = enriched;
      }

      setModalStaffOptions(staffMap);
    } catch (err) {
      console.error('Error loading staff options for modal:', err);
      setModalResolveError('Failed to load staff options: ' + err.message);
    } finally {
      setModalStaffLoading(false);
    }
  };

  const closeModalConflict = () => {
    setModalResolvingId(null);
    setModalSelections({});
    setModalResolutionReason('');
    setModalResolveError('');
  };

  const handleModalSelection = (apptId, field, value) => {
    setModalSelections(prev => ({
      ...prev,
      [apptId]: { ...(prev[apptId] || {}), [field]: value },
    }));
  };

  const submitModalConflictResolution = async (leaveItem) => {
    if (!modalResolutionReason.trim()) {
      setModalResolveError('Reason is required.');
      return;
    }
    const appointments = leaveItem.conflictingAppointments || [];
    const unresolved = appointments.filter(a => !modalSelections[a.id]?.action);
    if (unresolved.length) {
      setModalResolveError('Please select an action for every appointment.');
      return;
    }
    const missingStaff = appointments.filter(
      a => modalSelections[a.id]?.action === 'reassign' && !modalSelections[a.id]?.staffId
    );
    if (missingStaff.length) {
      setModalResolveError('Please select a replacement staff member for each reassigned appointment.');
      return;
    }

    setModalResolvingSaving(true);
    setModalResolveError('');
    try {
      for (const appt of appointments) {
        const sel = modalSelections[appt.id];
        const apptRef = doc(db, 'appointments', appt.id);
        if (sel.action === 'reassign') {
          await updateDoc(apptRef, {
            staffId:                 sel.staffId,
            staffName:               sel.staffName,
            previousStaffId:         leaveItem.staffId,
            previousStaffName:       leaveItem.staffName,
            staffReassigned:         true,
            staffReassignmentReason: modalResolutionReason.trim(),
            updatedAt:               serverTimestamp(),
          });
        } else {
          await updateDoc(apptRef, {
            status:             'cancelled',
            cancellationReason: `Staff availability: ${modalResolutionReason.trim()}`,
            updatedAt:          serverTimestamp(),
          });
        }
      }
      // Mark ALL leave docs for this conflict (including duplicates) as resolved
      const allLeaveIds = leaveItem._allIds?.length ? leaveItem._allIds : [leaveItem.id];
      await Promise.all(allLeaveIds.map(lid =>
        updateDoc(doc(db, 'leave', lid), {
          conflictsResolved:   true,
          conflictsResolvedAt: serverTimestamp(),
          conflictsResolvedBy: 'admin',
        })
      ));
      allLeaveIds.forEach(lid => dismissAlert(lid));
      closeModalConflict();
    } catch (err) {
      setModalResolveError('Failed to save: ' + err.message);
    } finally {
      setModalResolvingSaving(false);
    }
  };

  // Redirect staff-role users to the staff portal
  useEffect(() => {
    if (currentUser?.role === 'staff') {
      navigate(`/BespokeBeauty/${salonId}/staff/portal`, { replace: true });
    }
  }, [currentUser?.role, salonId, navigate]);

  // Configuration update handler - using useCallback
  const handleConfigUpdate = useCallback((section, newData) => {
    console.log('=== CONFIG UPDATE DEBUG ===');
    console.log('Section:', section);
    console.log('New data:', newData);
    if (section === 'aboutUs') {
      let aboutUsValue = newData;
      if (typeof newData !== 'string') {
        aboutUsValue = String(newData || '');
      }
      setSalonConfig(prevConfig => ({
        ...prevConfig,
        aboutUs: aboutUsValue
      }));
    } else if (section === 'businessHours') {
      setSalonConfig(prevConfig => ({
        ...prevConfig,
        [section]: newData
      }));
    } else {
      setSalonConfig(prevConfig => ({
        ...prevConfig,
        [section]: {
          ...prevConfig[section],
          ...newData
        }
      }));
    }
  }, []);

  // Save changes handler - using useCallback
  const saveChanges = useCallback(async () => {
    setSaving(true);
    try {
      const configToSave = {
        ...salonConfig,
        salonId: salonId,
        aboutUs: typeof salonConfig.aboutUs === 'string' ? salonConfig.aboutUs : String(salonConfig.aboutUs || '')
      };
      console.log('=== SAVING CONFIG ===');
      console.log('Hero image in config:', configToSave.branding?.images?.hero);
      console.log('Full config to save:', JSON.stringify(configToSave, null, 2));
      console.log('Saving salon configuration...');
      await updateSalonConfig(salonId, configToSave);
      setSavedMessage('Changes saved successfully!');
      setTimeout(() => setSavedMessage(''), 3000);
    } catch (error) {
      console.error("Failed to save changes for", salonId, ":", error);
      setSavedMessage('Error saving changes. Please try again.');
      setTimeout(() => setSavedMessage(''), 3000);
    }
    setSaving(false);
  }, [salonConfig, salonId]);

  // #Revised UI_v2 — TopNav: horizontal sticky tab bar (replaces renderGroupedSidebar / A2 vertical sidebar)
  const renderTopTabBar = () => {
    const isItemActive = (item) => {
      if (item.isOverview) return isModuleOverview;
      if (item.tabId === null) return activeModule === item.moduleId && !activeTab && !isModuleOverview;
      return activeModule === item.moduleId && activeTab === item.tabId;
    };

    const activeGroup = SIDEBAR_GROUPS.find(g => g.id === activeGroupId);

    return (
      <div className="admin-top-tab-bar">
        {/* Row 1: module group tabs */}
        <div className="tab-bar-groups">
          {SIDEBAR_GROUPS.map(group => {
            const hasActive = group.items.some(isItemActive);
            const isSelected = activeGroupId === group.id;
            return (
              <button
                key={group.id}
                className={`tab-group-btn${isSelected ? ' selected' : ''}${hasActive ? ' has-active' : ''}`}
                onClick={() => {
                  const firstItem = group.items[0];
                  if (firstItem && !isSelected) {
                    handleSidebarNavigation(firstItem.moduleId, firstItem.tabId, firstItem.isOverview);
                  }
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                  setActiveGroupId(isSelected ? null : group.id);
                }}
              >
                <span className="tab-group-icon">{group.icon}</span>
                <span className="tab-group-label">{group.label}</span>
              </button>
            );
          })}
        </div>
        {/* Row 2: sub-tool pills for the selected group */}
        {activeGroup && (
          <div className="tab-bar-items">
            {activeGroup.items.map(item => {
              const itemKey = item.tabId || `${item.moduleId}-overview`;
              const locked = (item.moduleId && !isModuleEnabled(item.moduleId)) ||
                (item.requiresTabAvailable && !isTabAvailable(item.tabId));
              const active = isItemActive(item);
              return (
                <button
                  key={itemKey}
                  className={`tab-item-btn${active ? ' active' : ''}${locked ? ' locked' : ''}`}
                  onClick={() => !locked && handleSidebarNavigation(item.moduleId, item.tabId, item.isOverview)}
                  disabled={locked}
                  title={locked && item.requiresTabAvailable ? `Requires the Bespoke AI module (${item.requiresLabel})` : undefined}
                >
                  <span>{item.label}</span>
                  {locked && (
                    <svg className="tab-lock-icon" viewBox="0 0 12 14" fill="none" width="11" height="11">
                      <rect x="1" y="6" width="10" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.3"/>
                      <path d="M3.5 6V4a2.5 2.5 0 015 0v2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render active editor
  const renderActiveEditor = () => {
    // Show SalonHomePage ONLY when on 'home' route with NO active tab
    // This is the landing page when admin logs in or clicks "Home" button
    if (activeModule === 'home' && !activeTab && !isModuleOverview) {
      return (
        // #Revised UI_v2 — A4: start: home screen with onboarding checklist widget
        <div>
          <OnboardingChecklist
            salonConfig={salonConfig}
            services={services}
            salonId={salonId}
            onNavigate={handleSidebarNavigation}
            isModuleEnabled={_isModuleEnabled}
          />
          <SalonHomePage salonId={salonId} />
        </div>
        // #Revised UI_v2 — A4: end
      );
    }

    if (isModuleOverview || !activeTab) {
      return <ModuleManager salonId={salonId} />;
    }

    if (moduleAccessDenied) {
      return (
        <div className="module-access-denied">
          <div className="access-denied-content">
            <div className="denied-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <h3>Module Access Restricted</h3>
            <p>This module is not available in your current subscription plan.</p>
            <p>Contact sales to upgrade and unlock this feature.</p>
            <div className="denied-actions">
              <button 
                className="contact-sales-button"
                onClick={() => window.open('https://www.bespokecrm.cloud/contact-us', '_blank')}
              >
                Contact Sales
              </button>
              <button 
                className="return-button"
                onClick={() => handleModuleNavigation(null)}
              >
                Return to Modules
              </button>
            </div>
          </div>
        </div>
      );
    }
    
    if (!isTabAvailable(activeTab)) {
      return (
        <div className="tab-not-available">
          <h3>Tab Not Available</h3>
          <p>The requested tab is not available in your current module configuration.</p>
          <button onClick={() => handleModuleNavigation(null)}>Return to Modules</button>
        </div>
      );
    }
    
    // All editor cases (unchanged)
    switch (activeTab) {
      case 'branding':
        return (
          <BrandingEditor
            branding={salonConfig.branding}
            onUpdate={(newData) => handleConfigUpdate('branding', newData)}
            salonId={salonId}
            onSave={saveChanges}
            saving={saving}
            savedMessage={savedMessage}
            onPreview={() => setShowPreviewModal(true)}
          />
        );
      case 'typography':
        return (
          <TypographyEditor
            typography={salonConfig.branding.typography}
            onUpdate={(newData) => handleConfigUpdate('branding', {
              ...salonConfig.branding,
              typography: newData
            })}
            salonId={salonId}
            onSave={saveChanges}
            saving={saving}
            savedMessage={savedMessage}
            onPreview={() => setShowPreviewModal(true)}
          />
        );
      case 'images':
        return (
          <ImageManager
            images={salonConfig.branding.images}
            onUpdate={(newData) => handleConfigUpdate('branding', {
              ...salonConfig.branding,
              images: newData
            })}
            salonId={salonId}
            onSave={saveChanges}
            saving={saving}
            savedMessage={savedMessage}
            onPreview={() => setShowPreviewModal(true)}
          />
        );
      case 'aboutUs':
        return (
          <AboutUsEditor
            aboutUs={typeof salonConfig.aboutUs === 'string' ? salonConfig.aboutUs : ''}
            onUpdate={(newData) => handleConfigUpdate('aboutUs', newData)}
            salonId={salonId}
            onSave={saveChanges}
            saving={saving}
            savedMessage={savedMessage}
            onPreview={() => setShowPreviewModal(true)}
          />
        );
      case 'contact':
        return (
          <ContactInfoEditor
            contact={salonConfig.contact}
            onUpdate={(newData) => handleConfigUpdate('contact', newData)}
            salonId={salonId}
            onPreview={() => setShowPreviewModal(true)}
          />
        );
      case 'hours':
        return (
          <BusinessHoursEditor
            salonId={salonId}
            onPreview={() => setShowPreviewModal(true)}
          />
        );
      case 'services':
        return (
          <ServicesEditor
            salonId={salonId}
            onUpdate={() => {
              setSavedMessage('Services saved successfully!');
              setTimeout(() => setSavedMessage(''), 3000);
            }}
            savedMessage={savedMessage}
            onPreview={() => setShowPreviewModal(true)}
          />
        );
      case 'products':
        return (
          <ProductsEditor
            salonId={salonId}
            onUpdate={() => {
              setSavedMessage('Products saved successfully!');
              setTimeout(() => setSavedMessage(''), 3000);
            }}
            savedMessage={savedMessage}
            onPreview={() => setShowPreviewModal(true)}
            onNavigateToCampaign={isModuleEnabled('communications') ? handleProductCampaign : null}
          />
        );
      case 'staff':
        return (
          <StaffEditor
            salonId={salonId}
            onUpdate={() => {
              setSavedMessage('Staff saved successfully!');
              setTimeout(() => setSavedMessage(''), 3000);
            }}
            savedMessage={savedMessage}
            onPreview={() => setShowPreviewModal(true)}
          />
        );
      case 'booking-calendar':
        return <BookingCalendarTab salonId={salonId} onPreview={() => setShowPreviewModal(true)} locationId={bookingLocationId} onLocationChange={setBookingLocationId} />;
      case 'booking-requests':
        return <BookingRequestsTab salonId={salonId} onPreview={() => setShowPreviewModal(true)} locationId={bookingLocationId} onLocationChange={setBookingLocationId} jump={bookingRequestsJump} onJumpConsumed={() => setBookingRequestsJump(null)} />;
      case 'leave-management':
        return <BookingLeaveManagement salonId={salonId} onConflictResolved={dismissAlert} />;
      case 'working-hours':
        return <WorkingHoursEditor salonId={salonId} />;
      case 'bookings-ai-agent':
        return <BookingsAIAgent salonId={salonId} onOpenCalendar={() => handleSidebarNavigation('bookings', 'booking-calendar')} />;
      case 'loyalty-ai-agent':
        return (
          <LoyaltyAIAgent
            salonId={salonId}
            onOpenPromotions={() => handleSidebarNavigation('promotions', 'promotions')}
            onOpenLoyaltyProgram={() => handleSidebarNavigation('promotions', 'loyalty-program')}
            onCreateCampaign={handleCreateCampaign}
          />
        );
      case 'reports-ai-agent':
        return (
          <ReportsAIAgent
            salonId={salonId}
            onOpenTargets={() => handleSidebarNavigation('reports', 'targets')}
            onCreateCampaign={handleCreateCampaign}
          />
        );
      case 'communications-ai-agent':
        return <CommunicationsAIAgent salonId={salonId} onOpenCampaignHistory={() => handleSidebarNavigation('communications', 'campaigns-listing')} />;
      case 'core-ai-agent':
        return <CoreAIAgent salonId={salonId} onNavigate={handleSidebarNavigation} />;
      case 'loyalty-program':
        return (
          <LoyaltyProgramEditor
            salonId={salonId}
            onUpdate={() => {
              setSavedMessage('Loyalty program settings saved successfully!');
              setTimeout(() => setSavedMessage(''), 3000);
            }}
          />
        );
      case 'promotions':
        return (
          <PromotionsEditor
            salonId={salonId}
            onUpdate={() => {
              setSavedMessage('Promotions saved successfully!');
              setTimeout(() => setSavedMessage(''), 3000);
            }}
            onCreateCampaign={handleCreateCampaign}
          />
        );
      case 'wallet-topups':
        return <WalletTopupsTab salonId={salonId} />;
      case 'file-integration':
        return <FileIntegrationTab salonId={salonId} />;
      case 'email-campaigns':
        return (
          <EmailCampaignsEditor
            salonId={salonId}
            prefillHtml={htmlEmailPrefill.html}
            prefillTextBody={htmlEmailPrefill.textBody}
            prefillSubject={htmlEmailPrefill.subject}
            onPrefillConsumed={() => setHtmlEmailPrefill({ html: '', textBody: '', subject: '', type: 'html' })}
            prefillCampaign={campaignEditPrefill.type === 'email' ? campaignEditPrefill.data : null}
            onCampaignPrefillConsumed={() => setCampaignEditPrefill({ type: null, data: null })}
            onUpdate={() => {
              setSavedMessage('Email campaign saved successfully!');
              setTimeout(() => setSavedMessage(''), 3000);
            }}
          />
        );
      case 'push-campaigns':
        return (
          <PushCampaignsEditor
            salonId={salonId}
            prefillSubject={pushPrefill.subject}
            prefillBody={pushPrefill.body}
            onPrefillConsumed={() => setPushPrefill({ subject: '', body: '' })}
            prefillCampaign={campaignEditPrefill.type === 'push' ? campaignEditPrefill.data : null}
            onCampaignPrefillConsumed={() => setCampaignEditPrefill({ type: null, data: null })}
            onUpdate={() => {
              setSavedMessage('Push campaign saved successfully!');
              setTimeout(() => setSavedMessage(''), 3000);
            }}
          />
        );
      case 'portal-personalisation':
        return (
          <PortalPersonalisationEditor
            salonId={salonId}
            onUpdate={() => {
              setSavedMessage('Portal personalisation saved successfully!');
              setTimeout(() => setSavedMessage(''), 3000);
            }}
          />
        );
      case 'whatsapp-campaigns':
        return (
          <WhatsAppCampaignsEditor
            salonId={salonId}
            prefillBody={whatsappPrefill.body}
            onPrefillConsumed={() => setWhatsappPrefill({ body: '' })}
            prefillCampaign={campaignEditPrefill.type === 'whatsapp' ? campaignEditPrefill.data : null}
            onCampaignPrefillConsumed={() => setCampaignEditPrefill({ type: null, data: null })}
            onUpdate={() => {
              setSavedMessage('WhatsApp campaign saved successfully!');
              setTimeout(() => setSavedMessage(''), 3000);
            }}
          />
        );
      case 'social-media-campaigns':
        return (
          <SocialMediaCampaignsEditor
            salonId={salonId}
            onUpdate={() => {
              setSavedMessage('Social media campaign recorded successfully!');
              setTimeout(() => setSavedMessage(''), 3000);
            }}
          />
        );
      case 'campaigns-listing':
        return (
          <CampaignsListingEditor
            salonId={salonId}
            onUpdate={() => {
              setSavedMessage('Campaign updated successfully!');
              setTimeout(() => setSavedMessage(''), 3000);
            }}
            onEditCampaign={handleEditCampaignFromListing}
          />
        );
      case 'customers':
        return <CustomersManager salonId={salonId} onPreview={() => setShowPreviewModal(true)} />;
      case 'query-builder':
        return <QueryBuilderTab salonId={salonId} />;
      case 'analytics-dashboard':
        return <AnalyticsDashboardTab salonId={salonId} />;
      case 'loyalty-ai':
        return <LoyaltyAI salonId={salonId} />;
      case 'communications-ai':
        return (
          <CommunicationsAI
            salonId={salonId}
            onUseInEmailEditor={handleUseInEmailEditor}
            onUseInWhatsAppEditor={handleUseInWhatsAppEditor}
            onUseInPushEditor={handleUseInPushEditor}
          />
        );
      case 'targets':
        return <TargetsEditor salonId={salonId} onPreview={() => setShowPreviewModal(true)} />;
      case 'staff-payroll':
        return <StaffPayrollTab salonId={salonId} onPreview={() => setShowPreviewModal(true)} />;
      case 'customCSS':
        return (
          <CustomCSSEditor
            customCSS={salonConfig.branding.customCSS || ''}
            onUpdate={(newCSS) => handleConfigUpdate('branding', {
              ...salonConfig.branding,
              customCSS: newCSS
            })}
            salonId={salonId}
            onSave={saveChanges}
            saving={saving}
            savedMessage={savedMessage}
            onPreview={() => setShowPreviewModal(true)}
          />
        );
      default:
        return (
          <div className="editor-not-found">
            <h3>Editor Not Found</h3>
            <p>The requested editor component could not be found.</p>
          </div>
        );
    }
  };

  // Get business hours for preview (unchanged)
  const getBusinessHoursForPreview = () => {
    if (salonConfig.businessHours?.regularHours) {
      return salonConfig.businessHours.regularHours;
    } else if (Array.isArray(salonConfig.businessHours)) {
      return salonConfig.businessHours;
    } else {
      return fallbackConfig.businessHours.regularHours;
    }
  };

  const businessHours = getBusinessHoursForPreview();

  // ── Preview: location-aware contact & hours ────────────────────────────
  // Use salonData (raw Firestore snapshot) so that businessHours.locations is not
  // destroyed by mergeWithFallback (which replaces businessHours when regularHours
  // is absent at the top level — the case for every multi-location salon).
  const previewLocations = salonData?.locations || salonConfig.locations || [];
  const isPreviewMultiLocation = previewLocations.length > 1;
  const previewActiveLocation = isPreviewMultiLocation
    ? (previewLocations.find(l => l.locationId === previewSelectedLocationId) || previewLocations[0])
    : null;

  const previewAddress = isPreviewMultiLocation
    ? (previewActiveLocation?.address || 'Address not provided')
    : (salonData?.address || 'Address not provided');
  const previewPhone = isPreviewMultiLocation
    ? (previewActiveLocation?.phone || 'Phone not provided')
    : (salonData?.phone || 'Phone not provided');
  const previewEmail = salonData?.email || 'Email not provided';

  const resolvePreviewHours = () => {
    // Use raw salonData.businessHours so per-location hours are intact
    const rawHours = salonData?.businessHours;
    if (isPreviewMultiLocation && rawHours?.locations) {
      const locId = previewActiveLocation?.locationId;
      const locHours = rawHours.locations[locId];
      if (locHours) {
        if (locHours.regularHours && Array.isArray(locHours.regularHours)) return locHours.regularHours;
        if (Array.isArray(locHours)) return locHours;
      }
    }
    return businessHours;
  };
  const previewBusinessHours = resolvePreviewHours();

  if (loading) {
    return <div className="admin-loading">Loading configuration for {salonId}...</div>;
  }

  return (
    <div className="admin-container">
      {/* Slide-in Menu */}
      {isMenuOpen && (
        <>
          <div
            className="admin-slide-in-menu"
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              width: '260px',
              height: '50px',
              backgroundColor: '#E64C31',
              zIndex: 1000,
              borderBottomLeftRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              padding: '0 20px'
            }}
          >
            <div
              className="admin-menu-dropdown"
              style={{
                position: 'absolute',
                top: '50px',
                right: 0,
                width: '300px',
                backgroundColor: '#E64C31',
                borderBottomLeftRadius: '10px',
                boxShadow: '-2px 2px 5px rgba(0,0,0,0.2)',
                overflow: 'hidden'
              }}
            >
              <div
                className="admin-menu-header"
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  padding: '15px'
                }}
              >
                <button
                  onClick={closeMenu}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'white',
                    fontSize: '24px',
                    cursor: 'pointer'
                  }}
                >
                  ✕
                </button>
              </div>

              {/* Menu options */}
              <div className="admin-menu-options">
                <button
                  onClick={() => {
                    handleModuleNavigation('home');
                    closeMenu();
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '15px',
                    color: 'white',
                    textDecoration: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    fontSize: '16px',
                    background: 'none',
                    border: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'Familjen Grotesk, sans-serif'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#c4402b'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Admin Home
                </button>

                <button
                  onClick={() => {
                    handleModuleNavigation('core');
                    closeMenu();
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '15px',
                    color: 'white',
                    textDecoration: 'none',
                    fontSize: '16px',
                    background: 'none',
                    border: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'Familjen Grotesk, sans-serif'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#c4402b'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Core Module
                </button>

                <button
                  onClick={() => {
                    handleModuleNavigation(null);
                    closeMenu();
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '15px',
                    color: 'white',
                    textDecoration: 'none',
                    fontSize: '16px',
                    background: 'none',
                    border: 'none',
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontFamily: 'Familjen Grotesk, sans-serif'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#c4402b'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  All Modules
                </button>

                <button
                  onClick={() => {
                    handleSignOut();
                    closeMenu();
                  }}
                  disabled={signingOut}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '15px',
                    color: 'white',
                    textDecoration: 'none',
                    background: 'none',
                    border: 'none',
                    textAlign: 'left',
                    cursor: signingOut ? 'not-allowed' : 'pointer',
                    opacity: signingOut ? 0.6 : 1,
                    fontSize: '16px',
                    fontFamily: 'Familjen Grotesk, sans-serif'
                  }}
                  onMouseEnter={(e) => !signingOut && (e.target.style.backgroundColor = '#c4402b')}
                  onMouseLeave={(e) => !signingOut && (e.target.style.backgroundColor = 'transparent')}
                >
                  {signingOut ? 'Signing out...' : 'Sign Out'}
                </button>
              </div>
            </div>
          </div>

          {/* Overlay */}
          <div
            onClick={closeMenu}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 999
            }}
          />
        </>
      )}

      {/* #Revised UI_v2 — A3: start: Enhanced Admin Header */}
      <div className="admin-header-logo">
        <div className="admin-header-logo-content">

          {/* Left: BespokeBeauty wordmark + SalonID */}
          <div className="admin-header-logo-text">
            <h2>BespokeBeauty</h2>
            <small>SalonID: {salonId}</small>
          </div>
          <div className="dashboard-title-with-links">
            <h1>Admin Portal</h1>
          </div>

          {/* Right zone: quick-actions + notification + profile */}
          <div className="header-right-zone">

            {/* Quick-action buttons */}
            {canManageBookings && (
              <button
                className="header-quick-action-btn"
                onClick={() => handleSidebarNavigation('bookings', 'booking-calendar')}
                title="Add new booking"
              >
                View Bookings
              </button>
            )}
            <button
              className="header-quick-action-btn"
              onClick={() => handleSidebarNavigation('core', 'customers')}
              title="Add new customer"
            >
              View Customer
            </button>

            {/* QR Voucher Scanner */}
            <button
              className="header-quick-action-btn"
              onClick={() => setShowQRScanner(true)}
              title="Scan customer voucher QR code"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
                <path d="M14 14h3v3h-3zM17 17h3v3h-3zM14 20h3"/>
              </svg>
              Scan Voucher
            </button>

            {/* Notification bell — only for users with booking access */}
            {canManageBookings && (() => {
              const activeBookings = pendingBookings.filter(b => !dismissedAlertIds.has(b.id));
              const activeLeaveApprovals = pendingLeaveApprovals.filter(l => !dismissedAlertIds.has(l.id));
              const activeConflicts = pendingConflictResolutions.filter(l => !dismissedAlertIds.has(l.id));
              const activeUpcoming = upcomingLeaveItems.filter(l => !dismissedAlertIds.has(l.id));
              const activeProductAlerts = productAlerts.filter(a => !dismissedAlertIds.has(a.id));
              const activeCancellations = customerCancellations.filter(c => !dismissedAlertIds.has(c.id));
              const totalAlerts = activeBookings.length + activeLeaveApprovals.length + activeConflicts.length + activeUpcoming.length + activeProductAlerts.length + activeCancellations.length;
              return (
                <button
                  className="header-notification-btn"
                  onClick={() => setShowAlertsModal(true)}
                  title={totalAlerts > 0 ? `${totalAlerts} outstanding alert${totalAlerts > 1 ? 's' : ''}` : 'No outstanding alerts'}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                  {totalAlerts > 0 && (
                    <span className="header-notification-badge">
                      {totalAlerts > 99 ? '99+' : totalAlerts}
                    </span>
                  )}
                </button>
              );
            })()}

            {/* Profile avatar + dropdown */}
            <div className="header-profile-wrapper" ref={profileMenuRef}>
              <button
                className="header-profile-btn"
                onClick={() => setShowProfileMenu(prev => !prev)}
                title="Admin profile"
              >
                {currentUser?.email ? currentUser.email[0].toUpperCase() : 'A'}
              </button>
              {showProfileMenu && (
                <div className="header-profile-menu">
                  <div className="header-profile-menu-header">
                    <p className="header-profile-menu-name">
                      {salonConfig?.branding?.name || 'Admin'}
                    </p>
                    <p className="header-profile-menu-email">
                      {currentUser?.email || ''}
                    </p>
                  </div>
                  <div className="header-profile-menu-divider" />
                  <button
                    className="header-profile-menu-item"
                    onClick={() => {
                      handleSidebarNavigation('core', 'branding');
                      setShowProfileMenu(false);
                    }}
                  >
                    ⚙ Salon Settings
                  </button>
                  <div className="header-profile-menu-divider" />
                  <button
                    className="header-profile-menu-item danger"
                    onClick={() => {
                      setShowProfileMenu(false);
                      handleSignOut();
                    }}
                    disabled={signingOut}
                  >
                    ↪ {signingOut ? 'Signing out...' : 'Sign Out'}
                  </button>
                </div>
              )}
            </div>

            {/* Mobile menu toggle (kept for small screens) */}
            <div
              onClick={toggleMenu}
              className="header-menu-toggle"
              style={{
                cursor: 'pointer',
                display: 'none',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span style={{ fontSize: '20px', fontFamily: 'Familjen Grotesk, sans-serif', color: 'white', fontWeight: '500' }}>Menu</span>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ width: '20px', height: '2px', backgroundColor: 'white', marginBottom: '3px' }}></span>
                <span style={{ width: '20px', height: '2px', backgroundColor: 'white' }}></span>
              </div>
            </div>

          </div>
        </div>
      </div>
      {/* #Revised UI_v2 — A3: end */}


      {/* Alerts modal */}
      {showAlertsModal && (() => {
        const activeBookings = pendingBookings.filter(b => !dismissedAlertIds.has(b.id));
        const activeLeaveApprovals = pendingLeaveApprovals.filter(l => !dismissedAlertIds.has(l.id));
        const activeConflicts = pendingConflictResolutions.filter(l => !dismissedAlertIds.has(l.id));
        const activeUpcoming = upcomingLeaveItems.filter(l => !dismissedAlertIds.has(l.id));
        const activeProductAlerts = productAlerts.filter(a => !dismissedAlertIds.has(a.id));
        const activeCancellations = customerCancellations.filter(c => !dismissedAlertIds.has(c.id));
        const totalAlerts = activeBookings.length + activeLeaveApprovals.length + activeConflicts.length + activeUpcoming.length + activeProductAlerts.length + activeCancellations.length;

        const fmtDate = (d) => d ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
        const fmtLeaveType = (t) => t === 'annual' ? 'Annual Leave' : t === 'medical' ? 'Medical Leave' : t ? 'Time Off' : '—';

        return (
          <div className="alerts-modal-overlay" onClick={() => setShowAlertsModal(false)}>
            <div className="alerts-modal" onClick={e => e.stopPropagation()}>
              <div className="alerts-modal-header">
                <h2 className="alerts-modal-title">
                  Outstanding Alerts
                  {totalAlerts > 0 && <span className="alerts-modal-count">{totalAlerts}</span>}
                </h2>
                <button className="alerts-modal-close" onClick={() => setShowAlertsModal(false)} aria-label="Close">✕</button>
              </div>

              {totalAlerts === 0 ? (
                <div className="alerts-modal-empty">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                  <p>No outstanding alerts</p>
                </div>
              ) : (
                <div className="alerts-modal-body">

                  {/* Pending booking requests */}
                  {activeBookings.length > 0 && (
                    <section className="alerts-section">
                      <div className="alerts-section-header">
                        <span className="alerts-section-dot coral" />
                        <h3>Pending Booking Requests <span className="alerts-section-count">{activeBookings.length}</span></h3>
                      </div>
                      <table className="alerts-table">
                        <thead>
                          <tr>
                            <th>Customer</th>
                            <th>Service</th>
                            <th>Staff</th>
                            <th>Date Requested</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeBookings.map(b => (
                            <tr key={b.id}>
                              <td>
                                <span className="alerts-table-primary">{b.customerName}</span>
                                {b.customerEmail && b.customerEmail !== b.customerName && (
                                  <span className="alerts-table-secondary">{b.customerEmail}</span>
                                )}
                              </td>
                              <td>{b.serviceName}</td>
                              <td>{b.staffName || '—'}</td>
                              <td>{fmtDate(b.dateRequested)}</td>
                              <td className="alerts-table-actions">
                                <button
                                  className="alerts-action-btn primary"
                                  onClick={() => {
                                    // Auto-select location pill for the appointment's location
                                    if (b.locationId) setBookingLocationId(b.locationId);
                                    // Compute the right time-period filter from the appointment date
                                    const appt = b.apptDate;
                                    let timeFilter = 'subsequent_weeks';
                                    if (appt) {
                                      const now = new Date();
                                      const day = now.getDay();
                                      const diffToMon = day === 0 ? -6 : 1 - day;
                                      const monday = new Date(now);
                                      monday.setDate(now.getDate() + diffToMon);
                                      monday.setHours(0, 0, 0, 0);
                                      const sunday = new Date(monday);
                                      sunday.setDate(monday.getDate() + 6);
                                      sunday.setHours(23, 59, 59, 999);
                                      if (appt >= monday && appt <= sunday) timeFilter = 'current_week';
                                      else if (appt > sunday) timeFilter = 'subsequent_weeks';
                                      else {
                                        const sevenDaysAgo = new Date(now); sevenDaysAgo.setDate(now.getDate() - 7);
                                        const oneMonthAgo  = new Date(now); oneMonthAgo.setMonth(now.getMonth() - 1);
                                        if (appt >= sevenDaysAgo) timeFilter = 'week';
                                        else if (appt >= oneMonthAgo) timeFilter = 'month';
                                        else timeFilter = 'year';
                                      }
                                    }
                                    setBookingRequestsJump({ tab: 'pending', timeFilter });
                                    setShowAlertsModal(false);
                                    handleSidebarNavigation('bookings', 'booking-requests');
                                  }}
                                >
                                  Review
                                </button>
                                <button className="alerts-action-btn ghost" onClick={() => dismissAlert(b.id)}>Dismiss</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </section>
                  )}

                  {/* Customer cancellations (customer cancelled their own appointment) */}
                  {activeCancellations.length > 0 && (
                    <section className="alerts-section">
                      <div className="alerts-section-header">
                        <span className="alerts-section-dot coral" />
                        <h3>Customer Cancellations <span className="alerts-section-count">{activeCancellations.length}</span></h3>
                      </div>
                      <table className="alerts-table">
                        <thead>
                          <tr>
                            <th>Customer</th>
                            <th>Service</th>
                            <th>Staff</th>
                            <th>Appointment Was</th>
                            <th>Cancelled</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeCancellations.map(c => (
                            <tr key={c.id}>
                              <td>
                                <span className="alerts-table-primary">{c.customerName}</span>
                                {c.customerEmail && c.customerEmail !== c.customerName && (
                                  <span className="alerts-table-secondary">{c.customerEmail}</span>
                                )}
                              </td>
                              <td>{c.serviceName}</td>
                              <td>{c.staffName || '—'}</td>
                              <td>{c.appointmentDate ? `${fmtDate(c.appointmentDate)} ${c.appointmentDate.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true })}` : '—'}</td>
                              <td>{fmtDate(c.cancelledAt)}</td>
                              <td className="alerts-table-actions">
                                <button
                                  className="alerts-action-btn primary"
                                  onClick={() => {
                                    setShowAlertsModal(false);
                                    handleSidebarNavigation('bookings', 'booking-requests');
                                  }}
                                >
                                  View
                                </button>
                                <button className="alerts-action-btn ghost" onClick={() => dismissAlert(c.id)}>Dismiss</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </section>
                  )}

                  {/* Pending leave approvals */}
                  {activeLeaveApprovals.length > 0 && (
                    <section className="alerts-section">
                      <div className="alerts-section-header">
                        <span className="alerts-section-dot coral" />
                        <h3>Leave Requests Pending Approval <span className="alerts-section-count">{activeLeaveApprovals.length}</span></h3>
                      </div>
                      <table className="alerts-table">
                        <thead>
                          <tr>
                            <th>Staff Member</th>
                            <th>Leave Type</th>
                            <th>Period</th>
                            <th>Reason</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeLeaveApprovals.map(l => (
                            <tr key={l.id}>
                              <td><span className="alerts-table-primary">{l.staffName}</span></td>
                              <td>{fmtLeaveType(l.type)}</td>
                              <td>{fmtDate(l.startDate)} – {fmtDate(l.endDate)}</td>
                              <td>{l.reason || '—'}</td>
                              <td className="alerts-table-actions">
                                <button
                                  className="alerts-action-btn primary"
                                  onClick={() => { setShowAlertsModal(false); handleSidebarNavigation('bookings', 'leave-management'); }}
                                >
                                  Review
                                </button>
                                <button className="alerts-action-btn ghost" onClick={() => dismissAlert(l.id)}>Dismiss</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </section>
                  )}

                  {/* Appointment conflicts needing reassignment */}
                  {activeConflicts.length > 0 && (
                    <section className="alerts-section">
                      <div className="alerts-section-header">
                        <span className="alerts-section-dot coral" />
                        <h3>Appointment Conflicts — Action Required <span className="alerts-section-count">{activeConflicts.length}</span></h3>
                      </div>

                      {activeConflicts.map(l => {
                        const isExpanded = modalResolvingId === l.id;
                        return (
                          <div key={l.id} style={{ border: '1px solid #f5c6cb', borderRadius: '8px', marginBottom: '10px', overflow: 'hidden' }}>
                            {/* Summary row */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: '#fff5f5', flexWrap: 'wrap' }}>
                              <span className="alerts-table-primary" style={{ minWidth: '120px' }}>{l.staffName}</span>
                              <span style={{ fontSize: '13px', color: '#555' }}>{fmtLeaveType(l.type)}</span>
                              <span style={{ fontSize: '13px', color: '#555' }}>{fmtDate(l.startDate)} – {fmtDate(l.endDate)}</span>
                              <span style={{ fontSize: '13px', color: '#856404', fontWeight: 600 }}>
                                ⚠ {l.conflictCount} appointment(s)
                              </span>
                              <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                                <button
                                  className="alerts-action-btn primary"
                                  onClick={() => isExpanded ? closeModalConflict() : openModalConflict(l)}
                                >
                                  {isExpanded ? 'Collapse ▲' : 'Reassign ▼'}
                                </button>
                                <button className="alerts-action-btn ghost" onClick={() => { dismissAlert(l.id); if (isExpanded) closeModalConflict(); }}>Dismiss</button>
                              </div>
                            </div>

                            {/* Inline reassignment panel */}
                            {isExpanded && (
                              <div style={{ padding: '14px 16px', background: '#fff' }}>

                                {modalStaffLoading ? (
                                  <p style={{ fontSize: '13px', color: '#888' }}>Loading available staff…</p>
                                ) : (
                                  <>
                                    {/* Mandatory reason */}
                                    <div style={{ marginBottom: '14px' }}>
                                      <label style={{ fontSize: '13px', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                                        Reason for reassignment / cancellation <span style={{ color: '#E64C31' }}>*</span>
                                      </label>
                                      <input
                                        type="text"
                                        value={modalResolutionReason}
                                        onChange={e => setModalResolutionReason(e.target.value)}
                                        placeholder="e.g. Staff is on approved annual leave"
                                        style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box' }}
                                      />
                                    </div>

                                    {/* One row per conflicting appointment */}
                                    {(l.conflictingAppointments || []).map(appt => {
                                      const apptDate = appt.dateTime?.toDate
                                        ? appt.dateTime.toDate()
                                        : appt.dateTime ? new Date(appt.dateTime) : null;
                                      const dateLabel = apptDate && !isNaN(apptDate)
                                        ? apptDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
                                        : '—';
                                      const timeLabel = apptDate && !isNaN(apptDate)
                                        ? apptDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
                                        : '';
                                      const sel = modalSelections[appt.id] || {};
                                      const staffList = modalStaffOptions[appt.id] || [];
                                      const availableStaff = staffList.filter(s => !s.unavailReason);
                                      const hasNoAvailable = availableStaff.length === 0;

                                      return (
                                        <div key={appt.id} style={{ background: '#fafafa', border: '1px solid #e8d8c0', borderRadius: '6px', padding: '12px', marginBottom: '10px' }}>
                                          <div style={{ fontSize: '13px', marginBottom: '8px' }}>
                                            <strong>{appt.customerName}</strong> — {appt.serviceName}
                                            <span style={{ color: '#888', marginLeft: '8px' }}>{dateLabel}{timeLabel ? ` at ${timeLabel}` : ''}</span>
                                          </div>

                                          {hasNoAvailable && staffList.length > 0 && (
                                            <p style={{ fontSize: '12px', color: '#856404', margin: '0 0 8px' }}>
                                              ⚠ No other staff available at this time. You may need to cancel this appointment.
                                            </p>
                                          )}
                                          {staffList.length === 0 && (
                                            <p style={{ fontSize: '12px', color: '#856404', margin: '0 0 8px' }}>
                                              ⚠ No other staff found for this service / location.
                                            </p>
                                          )}

                                          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                                            {/* Action selector */}
                                            <select
                                              value={sel.action || ''}
                                              onChange={e => handleModalSelection(appt.id, 'action', e.target.value)}
                                              style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}
                                            >
                                              <option value="">— Select action —</option>
                                              <option value="reassign">Reassign to another staff</option>
                                              <option value="cancel">Cancel appointment</option>
                                            </select>

                                            {/* Staff picker (only for reassign) */}
                                            {sel.action === 'reassign' && (
                                              <select
                                                value={sel.staffId || ''}
                                                onChange={e => {
                                                  const chosen = staffList.find(s => s.staffId === e.target.value);
                                                  handleModalSelection(appt.id, 'staffId',   e.target.value);
                                                  handleModalSelection(appt.id, 'staffName', chosen?.name || '');
                                                }}
                                                style={{ padding: '6px 10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '13px' }}
                                              >
                                                <option value="">— Select staff —</option>
                                                {staffList.map(s => (
                                                  <option key={s.staffId} value={s.staffId} disabled={!!s.unavailReason}>
                                                    {s.name}{s.unavailReason ? ` (${s.unavailReason})` : ''}
                                                  </option>
                                                ))}
                                              </select>
                                            )}

                                            {sel.action === 'cancel' && (
                                              <span style={{ fontSize: '12px', color: '#721c24' }}>
                                                Customer will be notified of the cancellation with the reason above.
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}

                                    {modalResolveError && (
                                      <p style={{ color: '#721c24', fontSize: '13px', margin: '8px 0' }}>{modalResolveError}</p>
                                    )}

                                    <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                      <button
                                        className="alerts-action-btn primary"
                                        onClick={() => submitModalConflictResolution(l)}
                                        disabled={modalResolvingSaving}
                                      >
                                        {modalResolvingSaving ? 'Saving…' : 'Confirm Resolution'}
                                      </button>
                                      <button className="alerts-action-btn ghost" onClick={closeModalConflict}>
                                        Cancel
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </section>
                  )}

                  {/* Upcoming leave this week */}
                  {activeUpcoming.length > 0 && (
                    <section className="alerts-section">
                      <div className="alerts-section-header">
                        <span className="alerts-section-dot amber" />
                        <h3>Upcoming Staff Leave This Week <span className="alerts-section-count">{activeUpcoming.length}</span></h3>
                      </div>
                      <table className="alerts-table">
                        <thead>
                          <tr>
                            <th>Staff Member</th>
                            <th>Leave Type</th>
                            <th>Period</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeUpcoming.map(l => (
                            <tr key={l.id}>
                              <td><span className="alerts-table-primary">{l.staffName}</span></td>
                              <td>{fmtLeaveType(l.type)}</td>
                              <td>{fmtDate(l.startDate)} – {fmtDate(l.endDate)}</td>
                              <td className="alerts-table-actions">
                                <button
                                  className="alerts-action-btn primary"
                                  onClick={() => { setShowAlertsModal(false); handleSidebarNavigation('bookings', 'leave-management'); }}
                                >
                                  View
                                </button>
                                <button className="alerts-action-btn ghost" onClick={() => dismissAlert(l.id)}>Dismiss</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </section>
                  )}

                  {/* Product Inventory Alerts */}
                  {activeProductAlerts.length > 0 && (
                    <section className="alerts-section">
                      <div className="alerts-section-header">
                        <span className="alerts-section-dot" style={{ background: '#e67e22' }} />
                        <h3>Product Inventory Alerts <span className="alerts-section-count">{activeProductAlerts.length}</span></h3>
                      </div>
                      <table className="alerts-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Alert</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {activeProductAlerts.map(a => (
                            <tr key={a.id}>
                              <td><span className="alerts-table-primary">{a.productName}</span></td>
                              <td>{a.message || (a.type === 'out_of_stock' ? 'Stock has reached 0' : 'Renewal date approaching')}</td>
                              <td className="alerts-table-actions">
                                <button
                                  className="alerts-action-btn primary"
                                  onClick={() => { setShowAlertsModal(false); handleSidebarNavigation('core', 'products'); }}
                                >
                                  View Inventory
                                </button>
                                <button className="alerts-action-btn ghost" onClick={() => {
                                  dismissAlert(a.id);
                                  resolveProductAlert(a.id).catch(() => {});
                                }}>Dismiss</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </section>
                  )}

                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* #Revised UI_v2 — TopNav: sticky horizontal module tab bar, placed between header and content */}
      {renderTopTabBar()}

      {/* Admin content — full-width, sidebar replaced by top tab bar */}
      <div className={`admin-content ${isFullWidthTab ? 'no-preview' : ''} ${isModuleOverview ? 'module-overview' : ''}`}>
        <div className="admin-editor full-width">
          {renderActiveEditor()}
        </div>
      </div>

      {/* Live Preview Modal */}
      {showPreviewModal && (
        <div className="preview-modal-overlay" onClick={() => setShowPreviewModal(false)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h2>Live Preview - Customer Home Page</h2>
              <button className="preview-modal-close" onClick={() => setShowPreviewModal(false)}>
                &times;
              </button>
            </div>
            <div className="preview-modal-content">
              <div className="preview-phone-frame" style={{ '--salon-primary': salonConfig.branding?.colors?.primary || '#E64C31', '--salon-heading-font': salonConfig.branding?.typography?.headingFont || 'Familjen Grotesk, sans-serif' }}>
                {/* Logo Section */}
                {salonConfig.branding?.logo?.url && (
                  <div className={`salon-logo-section logo-${salonConfig.branding?.logo?.position || 'center'}`}>
                    <img
                      src={salonConfig.branding.logo.url}
                      alt="Salon Logo"
                      className="salon-detail-logo"
                    />
                  </div>
                )}

                {/* Hero Image */}
                {salonConfig.branding?.images?.hero && (
                  <div className="salon-hero-section">
                    <img
                      src={salonConfig.branding.images.hero}
                      alt="Salon Hero"
                      className="salon-hero-image"
                    />
                  </div>
                )}

                {/* Gallery - Right after Hero */}
                {salonConfig.branding?.images?.gallery && salonConfig.branding.images.gallery.length > 0 && (
                  <div className="salon-gallery-section">
                    <h2 style={{ color: salonConfig.branding?.colors?.primary || '#E64C31' }}>Our Gallery</h2>
                    <div className="salon-gallery-grid">
                      {salonConfig.branding.images.gallery.map((image, index) => (
                        <img
                          key={index}
                          src={image}
                          alt={`Gallery ${index + 1}`}
                          className="salon-gallery-image"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Services Section */}
                <div className="services-section">
                  <h2>Our Services</h2>
                  {services.filter(s => s.isActive !== false).length > 0 ? (
                    <div className="preview-modal-services-carousel">
                      {deduplicateServicesByName(services.filter(s => s.isActive !== false)).map((service) => (
                        <div key={service.id} className="preview-modal-service-card">
                          {/* Image */}
                          <div
                            className="preview-modal-service-img"
                            style={{ background: `linear-gradient(135deg, ${salonConfig.branding?.colors?.secondary || '#FFBDB1'} 0%, ${salonConfig.branding?.colors?.primary || '#E64C31'} 100%)` }}
                          >
                            {service.imageUrl && <img src={service.imageUrl} alt={service.name} />}
                            {service.category && (
                              <span
                                className="preview-modal-service-cat"
                                style={{ color: salonConfig.branding?.colors?.primary || '#E64C31', borderColor: salonConfig.branding?.colors?.primary || '#E64C31' }}
                              >
                                {service.category}
                              </span>
                            )}
                          </div>
                          {/* Body */}
                          <div className="preview-modal-service-body">
                            <p className="preview-modal-service-name">{service.name}</p>
                            <div className="preview-modal-service-badges">
                              <span className="preview-modal-badge preview-modal-badge--duration">
                                ⏱ {service.duration || 'N/A'} min
                              </span>
                            </div>
                            {service.description && (
                              <p className="preview-modal-service-desc">{service.description}</p>
                            )}
                            <div
                              className="preview-modal-book-btn"
                              style={{ background: salonConfig.branding?.colors?.primary || '#E64C31' }}
                            >
                              Book Now
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: '#888', fontSize: '13px', padding: '0 16px' }}>No active services yet.</p>
                  )}
                </div>

                {/* Products Section */}
                {previewProducts.filter(p => p.isActive !== false).length > 0 && (
                  <div className="services-section">
                    <h2>Our Products</h2>
                    <div className="cd-products-grid preview-modal-products-row">
                      {previewProducts.filter(p => p.isActive !== false).map((product) => (
                        <div key={product.id} className="cd-product-card">
                          <div className="cd-product-image">
                            {product.imageUrl
                              ? <img src={product.imageUrl} alt={product.name} draggable={false} />
                              : <div className="cd-product-image-placeholder" />}
                            {product.category && (
                              <span className="cd-product-category">{product.category}</span>
                            )}
                          </div>
                          <div className="cd-product-body">
                            <p className="cd-product-name">{product.name}</p>
                            <div className="cd-product-badges">
                              <span className="cd-product-badge cd-product-badge--price">
                                ${Number(product.price || 0).toFixed(2)}
                              </span>
                            </div>
                            {product.description && (
                              <p className="cd-product-description">{product.description}</p>
                            )}
                            <div className="cd-product-actions">
                              <div className="cd-product-enquiry-btn" style={{ textAlign: 'center' }}>Make Enquiry</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* About Us Section */}
                <div className="about-us-section">
                  <h2>About Us</h2>
                  <p>{salonConfig.aboutUs || `At ${salonConfig.branding?.name || 'Our Salon'}, we believe in bringing out your natural beauty.`}</p>
                </div>

                {/* Location pills — interactive, controls Contact Us & Business Hours */}
                {previewLocations.length > 1 && (
                  <div className="location-pills-row">
                    <div className="location-pills">
                      {previewLocations.map((loc) => (
                        <button
                          key={loc.locationId}
                          className={`location-pill${loc.locationId === previewSelectedLocationId ? ' location-pill--active' : ''}`}
                          onClick={() => setPreviewSelectedLocationId(loc.locationId)}
                        >
                          {loc.locationName}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact Info Section — location-aware */}
                <div className="contact-info-section">
                  <h2>Contact Us</h2>
                  <div className="contact-details">
                    <p><strong>Address:</strong> {previewAddress}</p>
                    <p><strong>Phone:</strong> {previewPhone}</p>
                    <p><strong>Email:</strong> {previewEmail}</p>
                  </div>
                </div>

                {/* Business Hours Section — location-aware */}
                <div className="business-hours-section">
                  <h2>Business Hours</h2>
                  <ul className="hours-list">
                    {previewBusinessHours.map((hours) => (
                      <li key={hours.day}>
                        <span className="day">{hours.day}</span>
                        <span className="hours">
                          {hours.isClosed ? 'Closed' : `${formatTime(hours.open)} - ${formatTime(hours.close)}`}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Follow Us Section */}
                <div className="follow-us-section">
                  <h2>Follow Us</h2>
                  <div className="social-media-links">
                    {salonData?.socialMedia?.instagram && (
                      <a
                        href={salonData.socialMedia.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-media-link instagram"
                      >
                        <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                        <span>Instagram</span>
                      </a>
                    )}
                    {salonData?.socialMedia?.facebook && (
                      <a
                        href={salonData.socialMedia.facebook}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-media-link facebook"
                      >
                        <svg className="social-icon" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                        </svg>
                        <span>Facebook</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* QR Voucher Scanner Modal */}
      {showQRScanner && (
        <QRScannerModal onClose={() => setShowQRScanner(false)} />
      )}

      {/* Bespoke AI Hub — floating hub-and-spoke launcher */}
      <BespokeAIHub
        isTabAvailable={isTabAvailable}
        moduleConfig={moduleConfig}
        onNavigate={handleSidebarNavigation}
      />

      {/* BespokeBeauty copyright footer */}
      <div className="admin-portal-footer">
        BespokeBeauty™ © 2026. All Rights Reserved
      </div>

    </div>
  );
}

// Helper function to format time for the preview (unchanged)
function formatTime(time24) {
  if (!time24) return '';
  
  const [hours, minutes] = time24.split(':').map(Number);
  
  if (isNaN(hours) || isNaN(minutes)) return '';
  
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  
  return `${hours12}:${minutes.toString().padStart(2, '0')} ${period}`;
}

export default Admin;